(function polyfill() {
  const relList = document.createElement("link").relList;
  if (relList && relList.supports && relList.supports("modulepreload")) return;
  for (const link of document.querySelectorAll('link[rel="modulepreload"]')) processPreload(link);
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type !== "childList") continue;
      for (const node of mutation.addedNodes) if (node.tagName === "LINK" && node.rel === "modulepreload") processPreload(node);
    }
  }).observe(document, {
    childList: true,
    subtree: true
  });
  function getFetchOpts(link) {
    const fetchOpts = {};
    if (link.integrity) fetchOpts.integrity = link.integrity;
    if (link.referrerPolicy) fetchOpts.referrerPolicy = link.referrerPolicy;
    if (link.crossOrigin === "use-credentials") fetchOpts.credentials = "include";
    else if (link.crossOrigin === "anonymous") fetchOpts.credentials = "omit";
    else fetchOpts.credentials = "same-origin";
    return fetchOpts;
  }
  function processPreload(link) {
    if (link.ep) return;
    link.ep = true;
    const fetchOpts = getFetchOpts(link);
    fetch(link.href, fetchOpts);
  }
})();
let invariant = () => {
};
// @__NO_SIDE_EFFECTS__
function memo(callback) {
  let result;
  return () => {
    if (result === void 0)
      result = callback();
    return result;
  };
}
const noop = /* @__NO_SIDE_EFFECTS__ */ (any) => any;
const secondsToMilliseconds = /* @__NO_SIDE_EFFECTS__ */ (seconds) => seconds * 1e3;
const millisecondsToSeconds = /* @__NO_SIDE_EFFECTS__ */ (milliseconds) => milliseconds / 1e3;
const isBezierDefinition = /* @__NO_SIDE_EFFECTS__ */ (easing) => Array.isArray(easing) && typeof easing[0] === "number";
const generateLinearEasing = (easing, duration, resolution = 10) => {
  let points = "";
  const numPoints = Math.max(Math.round(duration / resolution), 2);
  for (let i = 0; i < numPoints; i++) {
    points += Math.round(easing(i / (numPoints - 1)) * 1e4) / 1e4 + ", ";
  }
  return `linear(${points.substring(0, points.length - 2)})`;
};
const isNotNull = (value) => value !== null;
function getFinalKeyframe(keyframes, { repeat, repeatType = "loop" }, finalKeyframe, speed = 1) {
  const resolvedKeyframes = keyframes.filter(isNotNull);
  const useFirstKeyframe = speed < 0 || repeat && repeatType !== "loop" && repeat % 2 === 1;
  const index = useFirstKeyframe ? 0 : resolvedKeyframes.length - 1;
  return !index || finalKeyframe === void 0 ? resolvedKeyframes[index] : finalKeyframe;
}
class WithPromise {
  constructor() {
    this.updateFinished();
  }
  get finished() {
    return this._finished;
  }
  updateFinished() {
    this._finished = new Promise((resolve) => {
      this.resolve = resolve;
    });
  }
  notifyFinished() {
    this.resolve();
  }
  /**
   * Allows the animation to be awaited.
   *
   * @deprecated Use `finished` instead.
   */
  then(onResolve, onReject) {
    return this.finished.then(onResolve, onReject);
  }
}
function fillWildcards(keyframes) {
  for (let i = 1; i < keyframes.length; i++) {
    keyframes[i] ?? (keyframes[i] = keyframes[i - 1]);
  }
}
const isCSSVar = (name) => name.startsWith("--");
function setStyle(element, name, value) {
  isCSSVar(name) ? element.style.setProperty(name, value) : element.style[name] = value;
}
const supportsFlags = {};
function memoSupports(callback, supportsFlag) {
  const memoized = /* @__PURE__ */ memo(callback);
  return () => supportsFlags[supportsFlag] ?? memoized();
}
const supportsScrollTimeline = /* @__PURE__ */ memoSupports(() => window.ScrollTimeline !== void 0, "scrollTimeline");
const supportsLinearEasing = /* @__PURE__ */ memoSupports(() => {
  try {
    document.createElement("div").animate({ opacity: 0 }, { easing: "linear(0, 1)" });
  } catch (e) {
    return false;
  }
  return true;
}, "linearEasing");
const cubicBezierAsString = ([a, b, c, d]) => `cubic-bezier(${a}, ${b}, ${c}, ${d})`;
const supportedWaapiEasing = {
  linear: "linear",
  ease: "ease",
  easeIn: "ease-in",
  easeOut: "ease-out",
  easeInOut: "ease-in-out",
  circIn: /* @__PURE__ */ cubicBezierAsString([0, 0.65, 0.55, 1]),
  circOut: /* @__PURE__ */ cubicBezierAsString([0.55, 0, 1, 0.45]),
  backIn: /* @__PURE__ */ cubicBezierAsString([0.31, 0.01, 0.66, -0.59]),
  backOut: /* @__PURE__ */ cubicBezierAsString([0.33, 1.53, 0.69, 0.99])
};
function mapEasingToNativeEasing(easing, duration) {
  if (!easing) {
    return void 0;
  } else if (typeof easing === "function") {
    return supportsLinearEasing() ? generateLinearEasing(easing, duration) : "ease-out";
  } else if (/* @__PURE__ */ isBezierDefinition(easing)) {
    return cubicBezierAsString(easing);
  } else if (Array.isArray(easing)) {
    return easing.map((segmentEasing) => mapEasingToNativeEasing(segmentEasing, duration) || supportedWaapiEasing.easeOut);
  } else {
    return supportedWaapiEasing[easing];
  }
}
function startWaapiAnimation(element, valueName, keyframes, { delay = 0, duration = 300, repeat = 0, repeatType = "loop", ease = "easeOut", times } = {}, pseudoElement = void 0) {
  const keyframeOptions = {
    [valueName]: keyframes
  };
  if (times)
    keyframeOptions.offset = times;
  const easing = mapEasingToNativeEasing(ease, duration);
  if (Array.isArray(easing))
    keyframeOptions.easing = easing;
  const options = {
    delay,
    duration,
    easing: !Array.isArray(easing) ? easing : "linear",
    fill: "both",
    iterations: repeat + 1,
    direction: repeatType === "reverse" ? "alternate" : "normal"
  };
  if (pseudoElement)
    options.pseudoElement = pseudoElement;
  return element.animate(keyframeOptions, options);
}
function isGenerator(type) {
  return typeof type === "function" && "applyToOptions" in type;
}
function applyGeneratorOptions({ type, ...options }) {
  if (isGenerator(type) && supportsLinearEasing()) {
    return type.applyToOptions(options);
  } else {
    options.duration ?? (options.duration = 300);
    options.ease ?? (options.ease = "easeOut");
  }
  return options;
}
class NativeAnimation extends WithPromise {
  constructor(options) {
    super();
    this.finishedTime = null;
    this.isStopped = false;
    this.manualStartTime = null;
    if (!options)
      return;
    const { element, name, keyframes, pseudoElement, allowFlatten = false, finalKeyframe, onComplete } = options;
    this.isPseudoElement = Boolean(pseudoElement);
    this.allowFlatten = allowFlatten;
    this.options = options;
    invariant(typeof options.type !== "string");
    const transition = applyGeneratorOptions(options);
    this.animation = startWaapiAnimation(element, name, keyframes, transition, pseudoElement);
    if (transition.autoplay === false) {
      this.animation.pause();
    }
    this.animation.onfinish = () => {
      this.finishedTime = this.time;
      if (!pseudoElement) {
        const keyframe = getFinalKeyframe(keyframes, this.options, finalKeyframe, this.speed);
        if (this.updateMotionValue) {
          this.updateMotionValue(keyframe);
        }
        setStyle(element, name, keyframe);
        this.animation.cancel();
      }
      onComplete?.();
      this.notifyFinished();
    };
  }
  play() {
    if (this.isStopped)
      return;
    this.manualStartTime = null;
    this.animation.play();
    if (this.state === "finished") {
      this.updateFinished();
    }
  }
  pause() {
    this.animation.pause();
  }
  complete() {
    this.animation.finish?.();
  }
  cancel() {
    try {
      this.animation.cancel();
    } catch (e) {
    }
  }
  stop() {
    if (this.isStopped)
      return;
    this.isStopped = true;
    const { state } = this;
    if (state === "idle" || state === "finished") {
      return;
    }
    if (this.updateMotionValue) {
      this.updateMotionValue();
    } else {
      this.commitStyles();
    }
    if (!this.isPseudoElement)
      this.cancel();
  }
  /**
   * WAAPI doesn't natively have any interruption capabilities.
   *
   * In this method, we commit styles back to the DOM before cancelling
   * the animation.
   *
   * This is designed to be overridden by NativeAnimationExtended, which
   * will create a renderless JS animation and sample it twice to calculate
   * its current value, "previous" value, and therefore allow
   * Motion to also correctly calculate velocity for any subsequent animation
   * while deferring the commit until the next animation frame.
   */
  commitStyles() {
    const element = this.options?.element;
    if (!this.isPseudoElement && element?.isConnected) {
      this.animation.commitStyles?.();
    }
  }
  get duration() {
    const duration = this.animation.effect?.getComputedTiming?.().duration || 0;
    return /* @__PURE__ */ millisecondsToSeconds(Number(duration));
  }
  get iterationDuration() {
    const { delay = 0 } = this.options || {};
    return this.duration + /* @__PURE__ */ millisecondsToSeconds(delay);
  }
  get time() {
    return /* @__PURE__ */ millisecondsToSeconds(Number(this.animation.currentTime) || 0);
  }
  set time(newTime) {
    const wasFinished = this.finishedTime !== null;
    this.manualStartTime = null;
    this.finishedTime = null;
    this.animation.currentTime = /* @__PURE__ */ secondsToMilliseconds(newTime);
    if (wasFinished) {
      this.animation.pause();
    }
  }
  /**
   * The playback speed of the animation.
   * 1 = normal speed, 2 = double speed, 0.5 = half speed.
   */
  get speed() {
    return this.animation.playbackRate;
  }
  set speed(newSpeed) {
    if (newSpeed < 0)
      this.finishedTime = null;
    this.animation.playbackRate = newSpeed;
  }
  get state() {
    return this.finishedTime !== null ? "finished" : this.animation.playState;
  }
  get startTime() {
    return this.manualStartTime ?? Number(this.animation.startTime);
  }
  set startTime(newStartTime) {
    this.manualStartTime = this.animation.startTime = newStartTime;
  }
  /**
   * Attaches a timeline to the animation, for instance the `ScrollTimeline`.
   */
  attachTimeline({ timeline, rangeStart, rangeEnd, observe: observe2 }) {
    if (this.allowFlatten) {
      this.animation.effect?.updateTiming({ easing: "linear" });
    }
    this.animation.onfinish = null;
    if (timeline && supportsScrollTimeline()) {
      this.animation.timeline = timeline;
      if (rangeStart)
        this.animation.rangeStart = rangeStart;
      if (rangeEnd)
        this.animation.rangeEnd = rangeEnd;
      return noop;
    } else {
      return observe2(this);
    }
  }
}
class GroupAnimation {
  constructor(animations) {
    this.stop = () => this.runAll("stop");
    this.animations = animations.filter(Boolean);
  }
  get finished() {
    return Promise.all(this.animations.map((animation) => animation.finished));
  }
  /**
   * TODO: Filter out cancelled or stopped animations before returning
   */
  getAll(propName) {
    return this.animations[0][propName];
  }
  setAll(propName, newValue) {
    for (let i = 0; i < this.animations.length; i++) {
      this.animations[i][propName] = newValue;
    }
  }
  attachTimeline(timeline) {
    const subscriptions = this.animations.map((animation) => animation.attachTimeline(timeline));
    return () => {
      subscriptions.forEach((cancel, i) => {
        cancel && cancel();
        this.animations[i].stop();
      });
    };
  }
  get time() {
    return this.getAll("time");
  }
  set time(time) {
    this.setAll("time", time);
  }
  get speed() {
    return this.getAll("speed");
  }
  set speed(speed) {
    this.setAll("speed", speed);
  }
  get state() {
    return this.getAll("state");
  }
  get startTime() {
    return this.getAll("startTime");
  }
  get duration() {
    return getMax(this.animations, "duration");
  }
  get iterationDuration() {
    return getMax(this.animations, "iterationDuration");
  }
  runAll(methodName) {
    this.animations.forEach((controls) => controls[methodName]());
  }
  play() {
    this.runAll("play");
  }
  pause() {
    this.runAll("pause");
  }
  cancel() {
    this.runAll("cancel");
  }
  complete() {
    this.runAll("complete");
  }
}
function getMax(animations, propName) {
  let max = 0;
  for (let i = 0; i < animations.length; i++) {
    const value = animations[i][propName];
    if (value !== null && value > max) {
      max = value;
    }
  }
  return max;
}
class GroupAnimationWithThen extends GroupAnimation {
  then(onResolve, _onReject) {
    return this.finished.finally(onResolve).then(() => {
    });
  }
}
const animationMaps = /* @__PURE__ */ new WeakMap();
const animationMapKey = (name, pseudoElement = "") => `${name}:${pseudoElement}`;
function getAnimationMap(element) {
  let map = animationMaps.get(element);
  if (!map) {
    map = /* @__PURE__ */ new Map();
    animationMaps.set(element, map);
  }
  return map;
}
function resolveTransition(transition, parentTransition) {
  if (transition?.inherit && parentTransition) {
    const { inherit: _, ...rest } = transition;
    return { ...parentTransition, ...rest };
  }
  return transition;
}
function getValueTransition(transition, key) {
  const valueTransition = transition?.[key] ?? transition?.["default"] ?? transition;
  if (valueTransition !== transition) {
    return resolveTransition(valueTransition, transition);
  }
  return valueTransition;
}
const cornerRadiusProps = [
  "borderTopLeftRadius",
  "borderTopRightRadius",
  "borderBottomRightRadius",
  "borderBottomLeftRadius"
];
const pxValues = /* @__PURE__ */ new Set([
  // Border props
  "borderWidth",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "borderRadius",
  ...cornerRadiusProps,
  // Positioning props
  "width",
  "maxWidth",
  "height",
  "maxHeight",
  "top",
  "right",
  "bottom",
  "left",
  "inset",
  "insetBlock",
  "insetBlockStart",
  "insetBlockEnd",
  "insetInline",
  "insetInlineStart",
  "insetInlineEnd",
  // Spacing props
  "padding",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "paddingBlock",
  "paddingBlockStart",
  "paddingBlockEnd",
  "paddingInline",
  "paddingInlineStart",
  "paddingInlineEnd",
  "margin",
  "marginTop",
  "marginRight",
  "marginBottom",
  "marginLeft",
  "marginBlock",
  "marginBlockStart",
  "marginBlockEnd",
  "marginInline",
  "marginInlineStart",
  "marginInlineEnd",
  // Typography
  "fontSize",
  // Misc
  "backgroundPositionX",
  "backgroundPositionY"
]);
function applyPxDefaults(keyframes, name) {
  for (let i = 0; i < keyframes.length; i++) {
    if (typeof keyframes[i] === "number" && pxValues.has(name)) {
      keyframes[i] = keyframes[i] + "px";
    }
  }
}
function resolveElements(elementOrSelector, scope, selectorCache) {
  if (elementOrSelector == null) {
    return [];
  }
  if (elementOrSelector instanceof EventTarget) {
    return [elementOrSelector];
  } else if (typeof elementOrSelector === "string") {
    let root = document;
    const elements = root.querySelectorAll(elementOrSelector);
    return elements ? Array.from(elements) : [];
  }
  return Array.from(elementOrSelector).filter((element) => element != null);
}
function getComputedStyle(element, name) {
  const computedStyle = window.getComputedStyle(element);
  return isCSSVar(name) ? computedStyle.getPropertyValue(name) : computedStyle[name];
}
function animateElements(elementOrSelector, keyframes, options, scope) {
  if (elementOrSelector == null) {
    return [];
  }
  const elements = resolveElements(elementOrSelector);
  const numElements = elements.length;
  const animationDefinitions = [];
  for (let i = 0; i < numElements; i++) {
    const element = elements[i];
    const elementTransition = { ...options };
    if (typeof elementTransition.delay === "function") {
      elementTransition.delay = elementTransition.delay(i, numElements);
    }
    for (const valueName in keyframes) {
      let valueKeyframes = keyframes[valueName];
      if (!Array.isArray(valueKeyframes)) {
        valueKeyframes = [valueKeyframes];
      }
      const valueOptions = {
        ...getValueTransition(elementTransition, valueName)
      };
      valueOptions.duration && (valueOptions.duration = /* @__PURE__ */ secondsToMilliseconds(valueOptions.duration));
      valueOptions.delay && (valueOptions.delay = /* @__PURE__ */ secondsToMilliseconds(valueOptions.delay));
      const map = getAnimationMap(element);
      const key = animationMapKey(valueName, valueOptions.pseudoElement || "");
      const currentAnimation = map.get(key);
      currentAnimation && currentAnimation.stop();
      animationDefinitions.push({
        map,
        key,
        unresolvedKeyframes: valueKeyframes,
        options: {
          ...valueOptions,
          element,
          name: valueName,
          allowFlatten: !elementTransition.type && !elementTransition.ease
        }
      });
    }
  }
  for (let i = 0; i < animationDefinitions.length; i++) {
    const { unresolvedKeyframes, options: animationOptions } = animationDefinitions[i];
    const { element, name, pseudoElement } = animationOptions;
    if (!pseudoElement && unresolvedKeyframes[0] === null) {
      unresolvedKeyframes[0] = getComputedStyle(element, name);
    }
    fillWildcards(unresolvedKeyframes);
    applyPxDefaults(unresolvedKeyframes, name);
    if (!pseudoElement && unresolvedKeyframes.length < 2) {
      unresolvedKeyframes.unshift(getComputedStyle(element, name));
    }
    animationOptions.keyframes = unresolvedKeyframes;
  }
  const animations = [];
  for (let i = 0; i < animationDefinitions.length; i++) {
    const { map, key, options: animationOptions } = animationDefinitions[i];
    const animation = new NativeAnimation(animationOptions);
    map.set(key, animation);
    animation.finished.finally(() => map.delete(key));
    animations.push(animation);
  }
  return animations;
}
const createScopedWaapiAnimate = (scope) => {
  function scopedAnimate(elementOrSelector, keyframes, options) {
    return new GroupAnimationWithThen(animateElements(elementOrSelector, keyframes, options));
  }
  return scopedAnimate;
};
const animateMini = /* @__PURE__ */ createScopedWaapiAnimate();
const thresholds = {
  some: 0,
  all: 1
};
function inView(elementOrSelector, onStart, { root, margin: rootMargin, amount = "some" } = {}) {
  const elements = resolveElements(elementOrSelector);
  const activeIntersections = /* @__PURE__ */ new WeakMap();
  const onIntersectionChange = (entries) => {
    entries.forEach((entry) => {
      const onEnd = activeIntersections.get(entry.target);
      if (entry.isIntersecting === Boolean(onEnd))
        return;
      if (entry.isIntersecting) {
        const newOnEnd = onStart(entry.target, entry);
        if (typeof newOnEnd === "function") {
          activeIntersections.set(entry.target, newOnEnd);
        } else {
          observer.unobserve(entry.target);
        }
      } else if (typeof onEnd === "function") {
        onEnd(entry);
        activeIntersections.delete(entry.target);
      }
    });
  };
  const observer = new IntersectionObserver(onIntersectionChange, {
    root,
    rootMargin,
    threshold: typeof amount === "number" ? amount : thresholds[amount]
  });
  elements.forEach((element) => observer.observe(element));
  return () => observer.disconnect();
}
const STYLES = {
  info: "color:#4A90D9",
  warn: "color:#D9A441",
  error: "color:#D95757",
  success: "color:#4CAF50"
};
function consoleMessage(message, level = "info", payload) {
  if (level !== "error" && !window.STOREFRONT?.settings?.devMode) return;
  const _args = [`%c[Refuge] ${message}`, STYLES[level]];
  if (payload !== void 0) _args.push(payload);
  console[level === "success" ? "log" : level](..._args);
}
const HOST_SELECTOR = "product-form-component, [data-cart-bundle]";
const SUBMIT_SELECTOR = '[data-action="cart-submit"]';
const ERROR_SELECTOR = "[data-cart-error]";
const STATES = ["is-loading", "is-added", "is-error"];
const ADD_TO_BAG_ADDED_HOLD = 1600;
const ERROR_HOLD = 6e3;
const FALLBACK_ERROR = "That could not be added to your bag.";
function initAddToBagFeedback() {
  const _timers = /* @__PURE__ */ new WeakMap();
  const _messages = /* @__PURE__ */ new WeakMap();
  const _inFlight = /* @__PURE__ */ new WeakSet();
  document.addEventListener(
    "submit",
    (event) => {
      const _host = event.target?.closest(HOST_SELECTOR);
      if (!_host || !_inFlight.has(_host)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );
  const reset = (button) => {
    const _timer = _timers.get(button);
    if (_timer !== void 0) window.clearTimeout(_timer);
    button.classList.remove(...STATES);
    button.removeAttribute("aria-busy");
  };
  const hold = (button, state, ms) => {
    reset(button);
    button.classList.add(state);
    _timers.set(
      button,
      window.setTimeout(() => reset(button), ms)
    );
  };
  const showError = (host, message) => {
    const _field = host.querySelector(ERROR_SELECTOR);
    if (!_field) return;
    const _timer = _timers.get(_field);
    if (_timer !== void 0) window.clearTimeout(_timer);
    _field.textContent = message;
    _field.hidden = false;
    _timers.set(
      _field,
      window.setTimeout(() => {
        _field.hidden = true;
        _field.textContent = "";
      }, ERROR_HOLD)
    );
  };
  const clearError = (host) => {
    const _field = host.querySelector(ERROR_SELECTOR);
    if (!_field) return;
    const _timer = _timers.get(_field);
    if (_timer !== void 0) window.clearTimeout(_timer);
    _field.hidden = true;
    _field.textContent = "";
  };
  document.addEventListener("shopify:cart:error", (event) => {
    const _host = event.target?.closest(HOST_SELECTOR);
    if (!_host) return;
    _messages.set(_host, event.error?.trim() || FALLBACK_ERROR);
  });
  document.addEventListener("shopify:cart:lines-update", (event) => {
    const _event = event;
    if (_event.action !== "add") return;
    const _host = event.target?.closest(HOST_SELECTOR);
    const _button = _host?.querySelector(SUBMIT_SELECTOR);
    if (!_host || !_button) return;
    _messages.delete(_host);
    clearError(_host);
    _inFlight.add(_host);
    reset(_button);
    _button.classList.add("is-loading");
    _button.setAttribute("aria-busy", "true");
    _event.promise?.then(({ detail }) => {
      if (detail?.didError) {
        hold(_button, "is-error", ERROR_HOLD);
        showError(_host, _messages.get(_host) ?? FALLBACK_ERROR);
        return;
      }
      hold(_button, "is-added", ADD_TO_BAG_ADDED_HOLD);
    }).catch((error) => {
      hold(_button, "is-error", ERROR_HOLD);
      showError(_host, _messages.get(_host) ?? FALLBACK_ERROR);
      consoleMessage("AddToBagFeedback: add rejected", "error", error);
    }).finally(() => {
      _inFlight.delete(_host);
      _messages.delete(_host);
    });
  });
  consoleMessage("Add to cart feedback initialised", "info");
}
class CartLinesUpdateSignal extends Event {
  constructor(promise, addedLines = 1) {
    super("shopify:cart:lines-update", { bubbles: true, composed: true });
    this.action = "add";
    this.promise = promise;
    this.addedLines = addedLines;
  }
}
class CartErrorSignal extends Event {
  constructor(error) {
    super("shopify:cart:error", { bubbles: true, composed: true });
    this.error = error;
  }
}
const CART_DRAWER_SECTION = "refuge-cart-drawer";
const TIMING = {
  debounce: 150,
  resizeDebounce: 250,
  /** A photograph dissolving into the next: swatches, the gallery, the menu. */
  photoFade: 350
};
const GHOST = "refuge-photo-ghost";
const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
const LOAD_CEILING = 1200;
const turns = /* @__PURE__ */ new WeakMap();
const COPIED = [
  "objectFit",
  "objectPosition",
  "boxSizing",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "backgroundColor"
];
function swapPhoto({ image, swap, incoming, after = image }) {
  const _parent = after.parentElement;
  const _reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const _ghosts = _parent ? Array.from(_parent.querySelectorAll(`:scope > .${GHOST}`)) : [];
  if (_reduced || !_parent) {
    _ghosts.forEach((ghost) => ghost.remove());
    swap();
    return;
  }
  let _ghost = _ghosts.pop() ?? null;
  _ghosts.forEach((ghost) => ghost.remove());
  if (_ghost) {
    holdAt(_ghost);
  } else if (image.complete && image.naturalWidth > 0) {
    _ghost = outgoingCopy(image);
    after.after(_ghost);
    place(_ghost, image);
  }
  swap();
  if (!_ghost) return;
  const _turn = (turns.get(_ghost) ?? 0) + 1;
  turns.set(_ghost, _turn);
  const _copy = _ghost;
  ready(incoming?.() ?? image).then(() => {
    if (!_copy.isConnected || turns.get(_copy) !== _turn) return;
    const _fade = _copy.animate([{ opacity: _copy.style.opacity || "1" }, { opacity: 0 }], {
      duration: TIMING.photoFade,
      easing: EASE,
      // Held at nothing until removed, so the copy cannot snap back for a frame.
      fill: "forwards"
    });
    _fade.onfinish = () => _copy.remove();
  });
}
function holdAt(ghost) {
  const _opacity = window.getComputedStyle(ghost).opacity;
  ghost.getAnimations().forEach((animation) => animation.cancel());
  ghost.style.opacity = _opacity;
}
function outgoingCopy(image) {
  const _ghost = document.createElement("img");
  const _style = window.getComputedStyle(image);
  _ghost.src = image.currentSrc || image.src;
  _ghost.alt = "";
  _ghost.className = GHOST;
  _ghost.setAttribute("aria-hidden", "true");
  COPIED.forEach((property) => {
    _ghost.style[property] = _style[property];
  });
  return _ghost;
}
function place(ghost, image) {
  const _box = image.getBoundingClientRect();
  const _host = ghost.offsetParent ?? document.documentElement;
  const _frame = _host.getBoundingClientRect();
  ghost.style.top = `${_box.top - _frame.top - _host.clientTop + _host.scrollTop}px`;
  ghost.style.left = `${_box.left - _frame.left - _host.clientLeft + _host.scrollLeft}px`;
  ghost.style.width = `${_box.width}px`;
  ghost.style.height = `${_box.height}px`;
}
function ready(image) {
  const _decoded = image.decode().catch(() => void 0);
  const _ceiling = new Promise((resolve) => window.setTimeout(resolve, LOAD_CEILING));
  return Promise.race([_decoded, _ceiling]);
}
const WIDTHS = [400, 800, 1200, 1600];
const CARD = "[data-card-colors]";
function initCardColors() {
  document.addEventListener("click", (event) => {
    const _target = event.target;
    const _button = _target?.closest("[data-card-color]");
    if (!_button) return;
    const _card = _button.closest(CARD);
    if (!_card) return;
    const _colors = readColors(_card);
    const _wanted = _button.dataset.cardColor ?? "";
    const _entry = _colors?.[_wanted];
    if (!_entry) return;
    apply(_card, _button, _entry);
  });
  document.addEventListener("change", (event) => {
    const _input = event.target?.closest(
      '[data-colors] input[type="radio"]'
    );
    if (!_input?.checked) return;
    const _card = _input.closest(CARD);
    if (!_card) return;
    const _colors = readColors(_card);
    const _entry = _colors?.[_input.value.trim().toLowerCase()];
    if (!_entry) return;
    const _mark = _card.querySelector(
      `[data-card-color="${CSS.escape(_input.value.trim().toLowerCase())}"]`
    );
    apply(_card, _mark, _entry);
  });
}
function readColors(card) {
  const _raw = card.dataset.cardColors;
  if (!_raw) return null;
  try {
    return JSON.parse(_raw);
  } catch (error) {
    consoleMessage("cardColors: colour map is not valid JSON", "error", error);
    return null;
  }
}
function apply(card, button, entry) {
  const _image = card.querySelector("img");
  if (_image) {
    const [_w, _h] = (card.dataset.cardPad ?? "437x546").split("x").map(Number);
    const _ratio = _w && _h ? _h / _w : 1;
    const _src = sized(entry.src, 800, Math.round(800 * _ratio));
    if (filePath(_image.currentSrc || _image.src) !== filePath(_src)) {
      swapPhoto({
        image: _image,
        swap: () => {
          _image.src = _src;
          _image.srcset = WIDTHS.map(
            (width) => `${sized(entry.src, width, Math.round(width * _ratio))} ${width}w`
          ).join(", ");
        }
      });
    }
  }
  card.querySelectorAll('a[href*="/products/"]').forEach((link) => {
    link.href = entry.url;
  });
  card.querySelectorAll("[data-card-color]").forEach((swatch) => {
    if (swatch === button) {
      swatch.setAttribute("aria-current", "true");
    } else {
      swatch.removeAttribute("aria-current");
    }
  });
}
function filePath(src) {
  try {
    return new URL(src, window.location.origin).pathname;
  } catch {
    return src;
  }
}
function sized(src, width, height) {
  try {
    const _url = new URL(src, window.location.origin);
    _url.searchParams.set("width", String(width));
    _url.searchParams.set("height", String(height));
    _url.searchParams.set("pad_color", "ffffff");
    return _url.toString();
  } catch {
    return src;
  }
}
const CONSENT_FEATURE = { name: "consent-tracking-api", version: "0.1" };
const FEATURE_TIMEOUT = 2e3;
const CHOICES_SELECTOR = "[data-privacy-choices]";
const COOKIE_BAR_SELECTOR = '[data-section-type="cookie-bar"]';
const CONSENT_REOPEN = "consent:reopen";
const DONE_CLASS = "is-done";
function loadCustomerPrivacy() {
  const _existing = window.Shopify?.customerPrivacy ?? window.Shopify?.trackingConsent;
  if (_existing) return Promise.resolve(_existing);
  const _load = window.Shopify?.loadFeatures;
  if (!_load) return Promise.resolve(null);
  return new Promise((resolve) => {
    const _timer = window.setTimeout(() => resolve(null), FEATURE_TIMEOUT);
    const _settle = (api) => {
      window.clearTimeout(_timer);
      resolve(api);
    };
    try {
      _load([CONSENT_FEATURE], (error) => {
        if (error) {
          consoleMessage("Privacy: consent tracking API failed to load", "warn", error);
          _settle(null);
          return;
        }
        _settle(window.Shopify?.customerPrivacy ?? window.Shopify?.trackingConsent ?? null);
      });
    } catch (error) {
      consoleMessage("Privacy: consent tracking API threw on load", "warn", error);
      _settle(null);
    }
  });
}
function hasGlobalPrivacyControl() {
  return window.navigator.globalPrivacyControl === true;
}
function consentFor(granted) {
  return {
    analytics: granted,
    marketing: granted,
    preferences: granted,
    sale_of_data: granted && !hasGlobalPrivacyControl()
  };
}
async function readPrivacyState() {
  const _privacy = await loadCustomerPrivacy();
  const _state = {
    globalPrivacyControl: hasGlobalPrivacyControl(),
    consentApi: _privacy !== null,
    shopifyBanner: typeof window.privacyBanner?.showPreferences === "function"
  };
  if (!_privacy) return _state;
  try {
    return {
      ..._state,
      region: _privacy.getRegion?.(),
      saleOfDataRegion: _privacy.saleOfDataRegion?.(),
      saleOfDataAllowed: _privacy.saleOfDataAllowed?.(),
      consent: _privacy.currentVisitorConsent(),
      shouldShowBanner: _privacy.shouldShowBanner()
    };
  } catch (error) {
    consoleMessage("Privacy: could not read consent state", "warn", error);
    return _state;
  }
}
function confirmOptOut(trigger) {
  trigger.classList.add(DONE_CLASS);
  const _message = trigger.dataset.confirmation;
  if (_message) trigger.textContent = _message;
}
async function openPrivacyChoices(trigger) {
  if (document.querySelector(COOKIE_BAR_SELECTOR)) {
    window.STOREFRONT?.events?.dispatchEvent(new CustomEvent(CONSENT_REOPEN));
    return;
  }
  const _banner = window.privacyBanner;
  if (_banner?.showPreferences) {
    try {
      await _banner.showPreferences();
      return;
    } catch (error) {
      consoleMessage("Privacy: Shopify preferences modal failed to open", "warn", error);
    }
  }
  const _privacy = await loadCustomerPrivacy();
  if (!_privacy) {
    const _href = trigger.getAttribute("href");
    consoleMessage("Privacy: no consent API on the page — following the link", "warn", _href);
    if (_href) window.location.assign(_href);
    return;
  }
  if (window.Shopify?.designMode) {
    confirmOptOut(trigger);
    consoleMessage("Privacy: theme editor — opt-out not recorded", "warn");
    return;
  }
  try {
    _privacy.setTrackingConsent({ sale_of_data: false }, (response) => {
      if (response?.error) {
        consoleMessage("Privacy: Shopify rejected the opt-out", "error", response.error);
        return;
      }
      confirmOptOut(trigger);
      window.STOREFRONT?.events?.dispatchEvent(new CustomEvent("privacy:opt-out"));
      consoleMessage("Privacy: recorded a data sale opt-out", "success");
    });
  } catch (error) {
    consoleMessage("Privacy: could not write the opt-out", "error", error);
  }
}
function initPrivacyChoices() {
  document.addEventListener("click", (event) => {
    const _trigger = event.target?.closest(CHOICES_SELECTOR);
    if (!_trigger) return;
    event.preventDefault();
    void openPrivacyChoices(_trigger);
  });
}
function useDebounce(callback, delay = TIMING.debounce) {
  let _timer;
  const _debounced = (...args) => {
    if (_timer) clearTimeout(_timer);
    _timer = setTimeout(() => callback(...args), delay);
  };
  _debounced.cancel = () => {
    if (_timer) clearTimeout(_timer);
    _timer = void 0;
  };
  return _debounced;
}
function initGlobalEvents() {
  const _onResize = useDebounce(() => {
    window.STOREFRONT.events.dispatchEvent(
      new CustomEvent("viewport:resize", { detail: { width: window.innerWidth } })
    );
  }, TIMING.resizeDebounce);
  window.addEventListener("resize", _onResize, { passive: true });
  consoleMessage("Global events initialised", "info");
}
function guardElement(element, message) {
  if (element) return true;
  consoleMessage(message, "warn");
  return false;
}
function formatPrice(cents, moneyFormat) {
  const _amount = (cents / 100).toFixed(2);
  const _format = window.STOREFRONT?.theme?.moneyFormat ?? "${{amount}}";
  return _format.replace(/\{\{\s*amount\s*\}\}/, _amount);
}
function cacheElements(container, selectors) {
  const _entries = Object.entries(selectors);
  return _entries.reduce(
    (acc, [key, selector]) => {
      acc[key] = container.querySelector(selector);
      return acc;
    },
    {}
  );
}
const FORM_SELECTOR = "form[data-klaviyo-subscribe]";
const EMAIL_SELECTOR = 'input[name="contact[email]"]';
const ENDPOINT = "https://a.klaviyo.com/client/subscriptions/";
const REVISION = "2024-10-15";
function initKlaviyoSubscribe(config) {
  if (!config.companyId || !config.listId) return;
  document.addEventListener("submit", (event) => {
    const _form = event.target?.closest(FORM_SELECTOR);
    if (!_form) return;
    const _email = _form.querySelector(EMAIL_SELECTOR)?.value.trim();
    if (!_email) return;
    void sendSubscription(config, _email, _form.id || "refuge");
  });
}
async function sendSubscription(config, email, source) {
  try {
    const _response = await fetch(
      `${ENDPOINT}?company_id=${encodeURIComponent(config.companyId)}`,
      {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "application/json", revision: REVISION },
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: {
              custom_source: source,
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email,
                    subscriptions: {
                      email: { marketing: { consent: "SUBSCRIBED" } }
                    }
                  }
                }
              }
            },
            relationships: {
              list: { data: { type: "list", id: config.listId } }
            }
          }
        })
      }
    );
    if (!_response.ok) {
      consoleMessage(
        `Klaviyo refused the sign-up (${_response.status})`,
        "error",
        await _response.text()
      );
    }
  } catch (error) {
    consoleMessage("Klaviyo sign-up failed", "error", error);
  }
}
const CARD_SELECTOR = ".refuge-product-card--shoppable, .refuge-collection-tile--shoppable";
const PANEL_SELECTOR = ".refuge-quick-shop";
const OPEN_CLASS = "is-quick-shop-open";
function initQuickShopTouch() {
  if (!window.STOREFRONT.settings.theme.quickShopTouch) return;
  if (window.matchMedia("(hover: hover)").matches) return;
  let open = null;
  document.addEventListener("click", (event) => {
    const _target = event.target;
    const _card = _target?.closest(CARD_SELECTOR) ?? null;
    if (_card !== open) {
      open?.classList.remove(OPEN_CLASS);
      open = null;
    }
    if (!_card) return;
    if (_target?.closest(PANEL_SELECTOR)) return;
    if (_card === open) return;
    event.preventDefault();
    _card.classList.add(OPEN_CLASS);
    open = _card;
  });
  consoleMessage("Quick shop opens on tap", "info");
}
function whenIdle(callback, timeout = 2e3) {
  if (typeof window.requestIdleCallback === "function") {
    const _handle = window.requestIdleCallback(callback, { timeout });
    return () => window.cancelIdleCallback(_handle);
  }
  const _timer = setTimeout(callback, 1);
  return () => clearTimeout(_timer);
}
const watchers = /* @__PURE__ */ new Map();
function watcherFor(rootMargin) {
  const _existing = watchers.get(rootMargin);
  if (_existing) return _existing;
  const _callbacks = /* @__PURE__ */ new Map();
  const _observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const _callback = _callbacks.get(entry.target);
        if (!_callback) return;
        _observer.unobserve(entry.target);
        _callbacks.delete(entry.target);
        _callback();
      });
      if (_callbacks.size === 0) {
        _observer.disconnect();
        watchers.delete(rootMargin);
      }
    },
    { rootMargin }
  );
  const _watcher = { observer: _observer, callbacks: _callbacks };
  watchers.set(rootMargin, _watcher);
  return _watcher;
}
function whenVisible(element, callback, rootMargin = "200px") {
  if (typeof IntersectionObserver !== "function") {
    callback();
    return () => {
    };
  }
  if (element instanceof Element && element.getClientRects().length === 0) {
    callback();
    return () => {
    };
  }
  const _watcher = watcherFor(rootMargin);
  _watcher.callbacks.set(element, callback);
  _watcher.observer.observe(element);
  return () => {
    if (!_watcher.callbacks.delete(element)) return;
    _watcher.observer.unobserve(element);
    if (_watcher.callbacks.size === 0) {
      _watcher.observer.disconnect();
      watchers.delete(rootMargin);
    }
  };
}
function readSectionConfig(container) {
  const _raw = container.dataset.config;
  if (!_raw) return {};
  try {
    const _parsed = JSON.parse(_raw);
    if (!_parsed || typeof _parsed !== "object" || Array.isArray(_parsed)) return {};
    return _parsed;
  } catch (error) {
    consoleMessage("Section config is not valid JSON", "error", error);
    return {};
  }
}
const DEFAULTS = {
  revealEnabled: true,
  revealStagger: 80,
  quickShopTouch: true,
  debugLogging: false,
  klaviyoCompanyId: "",
  klaviyoListId: ""
};
function readThemeSettings() {
  const _node = document.getElementById("refuge-settings");
  if (!_node?.textContent) return { ...DEFAULTS };
  try {
    return { ...DEFAULTS, ...JSON.parse(_node.textContent) };
  } catch (error) {
    consoleMessage("Theme settings JSON is malformed; using defaults", "error", error);
    return { ...DEFAULTS };
  }
}
const REPORTED = {};
function report(name, value) {
  REPORTED[name] = value;
  window.STOREFRONT?.events.dispatchEvent(
    new CustomEvent("vitals:report", { detail: { name, value } })
  );
  consoleMessage(`${name}: ${Math.round(value)}${name === "CLS" ? "" : "ms"}`, "info");
}
function observe(type, handler) {
  try {
    const _observer = new PerformanceObserver((list) => handler(list.getEntries()));
    _observer.observe({ type, buffered: true });
  } catch {
  }
}
function reportWebVitals() {
  observe("largest-contentful-paint", (entries) => {
    const _last = entries[entries.length - 1];
    if (_last) report("LCP", _last.startTime);
  });
  let _cls = 0;
  observe("layout-shift", (entries) => {
    for (const entry of entries) {
      if (entry.hadRecentInput) continue;
      _cls += entry.value;
    }
    report("CLS", _cls);
  });
  let _inp = 0;
  observe("event", (entries) => {
    for (const entry of entries) {
      const _duration = entry.duration;
      if (_duration > _inp) {
        _inp = _duration;
        report("INP", _inp);
      }
    }
  });
}
function getWebVitals() {
  return { ...REPORTED };
}
class BaseComponent {
  /**
   * Only stores its arguments. Setup happens in `mount()` because a subclass's
   * field initialisers do not run until after `super()` returns — calling
   * `init()` from here would expose them as undefined.
   */
  constructor(container, config) {
    this.listeners = [];
    this.mounted = false;
    this.destroyed = false;
    this.container = container;
    this.overrides = config;
  }
  mount() {
    if (this.mounted || this.destroyed) return this;
    this.config = { ...this.getDefaultConfig(), ...this.overrides };
    this.mounted = true;
    this.init();
    return this;
  }
  addListener(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this.listeners.push({ target, type, handler, options });
  }
  log(message, level = "info", payload) {
    if (!this.config?.debug) return;
    consoleMessage(`[${this.componentName}] ${message}`, level, payload);
  }
  isMounted() {
    return this.mounted;
  }
  isDestroyed() {
    return this.destroyed;
  }
  destroy() {
    if (this.destroyed) return;
    this.listeners.forEach(({ target, type, handler, options }) => {
      target.removeEventListener(type, handler, options);
    });
    this.listeners = [];
    this.mounted = false;
    this.destroyed = true;
    this.log("destroyed");
  }
}
function mountComponent(Component, config) {
  return (container) => new Component(container, { ...readSectionConfig(container), ...config }).mount();
}
const SECTION_ATTRIBUTE = "data-section-type";
const typeSelector = (type) => `[${SECTION_ATTRIBUTE}~="${type}"]`;
const REGISTRY = /* @__PURE__ */ new Map();
const INSTANCES = /* @__PURE__ */ new Map();
const PENDING = /* @__PURE__ */ new Map();
function registerSection(type, lifecycle) {
  REGISTRY.set(type, lifecycle);
}
function sectionRoots(type, scope) {
  const _selector = typeSelector(type);
  const _roots = Array.from(scope.querySelectorAll(_selector));
  if (scope instanceof HTMLElement && scope.matches(_selector)) {
    _roots.unshift(scope);
  }
  return _roots;
}
const FALLBACK_KEYS = /* @__PURE__ */ new WeakMap();
let fallbackCounter = 0;
function instanceKey(type, root) {
  const _id = root.dataset.sectionId || root.id;
  if (_id) return `${type}:${_id}`;
  let _fallback = FALLBACK_KEYS.get(root);
  if (!_fallback) {
    fallbackCounter += 1;
    _fallback = `anonymous-${fallbackCounter}`;
    FALLBACK_KEYS.set(root, _fallback);
  }
  return `${type}:${_fallback}`;
}
function runMount(type, lifecycle, root) {
  const _key = instanceKey(type, root);
  PENDING.delete(_key);
  if (INSTANCES.has(_key)) return;
  try {
    INSTANCES.set(_key, lifecycle.onLoad?.(root));
  } catch (error) {
    consoleMessage(`Failed to load section "${type}"`, "error", error);
  }
}
const MOUNT_STRATEGIES = ["eager", "idle", "visible"];
function mountOverride(root) {
  const _named = root.dataset.sectionMount;
  return _named && MOUNT_STRATEGIES.includes(_named) ? _named : void 0;
}
function loadSection(type, lifecycle, root) {
  const _key = instanceKey(type, root);
  if (INSTANCES.has(_key) || PENDING.has(_key)) return;
  const _strategy = mountOverride(root) ?? lifecycle.mount ?? "eager";
  const _mount = () => runMount(type, lifecycle, root);
  if (_strategy === "eager") {
    _mount();
    return;
  }
  PENDING.set(
    _key,
    _strategy === "idle" ? whenIdle(_mount) : whenVisible(root, _mount)
  );
}
function unloadSection(type, lifecycle, root) {
  const _key = instanceKey(type, root);
  PENDING.get(_key)?.();
  PENDING.delete(_key);
  if (!INSTANCES.has(_key)) return;
  try {
    lifecycle.onUnload?.(root, INSTANCES.get(_key));
  } catch (error) {
    consoleMessage(`Failed to unload section "${type}"`, "error", error);
  } finally {
    INSTANCES.delete(_key);
  }
}
function eachRegisteredSection(scope, visit) {
  REGISTRY.forEach((lifecycle, type) => {
    sectionRoots(type, scope).forEach((root) => visit(type, lifecycle, root));
  });
}
function selectSection(type, lifecycle, root) {
  const _key = instanceKey(type, root);
  if (!INSTANCES.has(_key)) {
    PENDING.get(_key)?.();
    runMount(type, lifecycle, root);
  }
  lifecycle.onSelect?.(root, INSTANCES.get(_key));
}
function deselectSection(type, lifecycle, root) {
  lifecycle.onDeselect?.(root, INSTANCES.get(instanceKey(type, root)));
}
function blockTarget(event) {
  const _block = event.target;
  const _root = _block.closest(`[${SECTION_ATTRIBUTE}]`);
  if (!_root) return null;
  const _type = (_root.getAttribute(SECTION_ATTRIBUTE) ?? "").split(/\s+/).find((candidate) => REGISTRY.has(candidate));
  if (!_type) return null;
  return { type: _type, root: _root, block: _block };
}
function registerSectionLifecycles() {
  eachRegisteredSection(document, loadSection);
  document.addEventListener("shopify:section:load", (event) => {
    eachRegisteredSection(event.target, loadSection);
  });
  document.addEventListener("shopify:section:unload", (event) => {
    eachRegisteredSection(event.target, unloadSection);
  });
  document.addEventListener("shopify:section:select", (event) => {
    eachRegisteredSection(event.target, selectSection);
  });
  document.addEventListener("shopify:section:deselect", (event) => {
    eachRegisteredSection(event.target, deselectSection);
  });
  document.addEventListener("shopify:block:select", (event) => {
    const _target = blockTarget(event);
    if (!_target) return;
    REGISTRY.get(_target.type)?.onBlockSelect?.(
      _target.block,
      _target.root,
      INSTANCES.get(instanceKey(_target.type, _target.root))
    );
  });
  document.addEventListener("shopify:block:deselect", (event) => {
    const _target = blockTarget(event);
    if (!_target) return;
    REGISTRY.get(_target.type)?.onBlockDeselect?.(
      _target.block,
      _target.root,
      INSTANCES.get(instanceKey(_target.type, _target.root))
    );
  });
  consoleMessage(`Section lifecycles registered (${REGISTRY.size})`, "info", listSections());
}
function listSections() {
  return Array.from(REGISTRY.entries()).map(([type, lifecycle]) => ({
    type,
    mount: lifecycle.mount ?? "eager",
    found: sectionRoots(type, document).length,
    mounted: Array.from(INSTANCES.keys()).filter((key) => key.startsWith(`${type}:`)).length
  }));
}
function useSectionLifecycle(type, lifecycle) {
  registerSection(type, lifecycle);
}
const REDUCED_MOTION$3 = "(prefers-reduced-motion: reduce)";
const TRAVEL = [0.22, 1, 0.36, 1];
const VARIANTS = {
  up: {
    keyframes: { opacity: [0, 1], transform: ["translate3d(0, 16px, 0)", "none"] },
    duration: 0.8,
    ease: TRAVEL
  },
  left: {
    keyframes: { opacity: [0, 1], transform: ["translate3d(-40px, 0, 0)", "none"] },
    duration: 0.8,
    ease: TRAVEL
  },
  right: {
    keyframes: { opacity: [0, 1], transform: ["translate3d(40px, 0, 0)", "none"] },
    duration: 0.8,
    ease: TRAVEL
  },
  fade: { keyframes: { opacity: [0, 1] }, duration: 0.9, ease: "easeOut" },
  wipe: {
    keyframes: { clipPath: ["inset(0 0 0 100%)", "inset(0 0 0 0)"] },
    duration: 0.55,
    ease: TRAVEL
  },
  "wipe-up": {
    keyframes: { clipPath: ["inset(100% 0 0 0)", "inset(0 0 0 0)"] },
    duration: 0.55,
    ease: TRAVEL
  }
};
function variantFor(element) {
  return VARIANTS[element.dataset.reveal ?? ""] ?? VARIANTS.up;
}
class RevealOnScroll extends BaseComponent {
  constructor() {
    super(...arguments);
    this.componentName = "RevealOnScroll";
    this.teardowns = [];
    this.revealed = /* @__PURE__ */ new WeakSet();
  }
  getDefaultConfig() {
    const _theme = window.STOREFRONT?.settings?.theme;
    return {
      debug: false,
      target: "[data-reveal]",
      stagger: _theme?.revealStagger ?? 80,
      // The entry runs as the section approaches, not once it has arrived.
      // Measured against the bare viewport it waited for a fifth of the element
      // to be on screen, and on a tall one — the bed photograph is most of a
      // column — that is a long way down: you watched a clipped section sit
      // still, then wipe. A quarter of a viewport of lead time means the wipe
      // is mostly done by the time the section is properly in front of you.
      margin: "0px 0px 25% 0px"
    };
  }
  init() {
    const _targets = Array.from(this.container.querySelectorAll(this.config.target));
    const _elements = _targets.length > 0 ? _targets : [this.container];
    if (window.STOREFRONT?.settings?.theme?.revealEnabled === false || window.matchMedia(REDUCED_MOTION$3).matches || !document.documentElement.classList.contains("refuge-motion")) {
      _elements.forEach((element) => element.classList.add("is-revealed"));
      this.log("reveal disabled or reduced motion — revealed immediately");
      return;
    }
    if (this.container.dataset.revealTrigger === "load") {
      _elements.forEach((element, index) => {
        element.dataset.revealBound = "";
        this.reveal(element, index, () => {
        });
      });
      this.log(`revealing ${_elements.length} element(s) at load`);
      return;
    }
    _elements.forEach((element, index) => {
      element.dataset.revealBound = "";
      const _stop = inView(element, () => this.reveal(element, index, _stop), {
        amount: "some",
        margin: this.config.margin
      });
      this.teardowns.push(_stop);
    });
    this.log(`observing ${_elements.length} element(s)`);
  }
  reveal(element, index, stop) {
    if (this.revealed.has(element)) return;
    this.revealed.add(element);
    stop();
    const _variant = variantFor(element);
    const _animation = animateMini(element, _variant.keyframes, {
      duration: _variant.duration,
      ease: _variant.ease,
      delay: this.delayFor(element, index) / 1e3
    });
    void _animation.finished.then(() => element.classList.add("is-revealed")).catch(() => {
    });
    this.teardowns.push(() => _animation.stop());
  }
  /**
   * The design gives some entries a delay of their own — 150ms on the lifestyle
   * band's script line, 100ms on the bed panel — because it composes them
   * against the element beside them rather than against document order. Those
   * win over the stagger wherever they are set.
   */
  delayFor(element, index) {
    const _own = Number(element.dataset.revealDelay);
    return Number.isFinite(_own) && _own >= 0 ? _own : index * this.config.stagger;
  }
  destroy() {
    if (this.isDestroyed()) return;
    this.teardowns.forEach((teardown) => teardown());
    this.teardowns = [];
    super.destroy();
  }
}
useSectionLifecycle("reveal-on-scroll", {
  mount: "visible",
  onLoad: mountComponent(RevealOnScroll),
  onUnload: (_root, instance) => instance?.destroy()
});
const REDUCED_MOTION$2 = "(prefers-reduced-motion: reduce)";
const ROLL_MS = 1e3;
class PromotionBar extends BaseComponent {
  constructor() {
    super(...arguments);
    this.componentName = "PromotionBar";
    this.messages = [];
    this.index = 0;
    this.timer = null;
    this.settles = /* @__PURE__ */ new Map();
  }
  getDefaultConfig() {
    return { debug: false, intervalSeconds: 6 };
  }
  init() {
    this.messages = Array.from(
      this.container.querySelectorAll("[data-promotion-message]")
    );
    if (this.messages.length < 2) {
      this.log("single message — nothing to rotate");
      return;
    }
    if (window.matchMedia(REDUCED_MOTION$2).matches) {
      this.log("reduced motion — holding the first message");
      return;
    }
    this.addListener(this.container, "pointerenter", () => this.pause());
    this.addListener(this.container, "pointerleave", () => this.play());
    this.addListener(this.container, "focusin", () => this.pause());
    this.addListener(this.container, "focusout", () => this.play());
    this.play();
    this.log(`rotating ${this.messages.length} messages every ${this.intervalMs()}ms`);
  }
  intervalMs() {
    const _seconds = Number(this.config.intervalSeconds);
    return Number.isFinite(_seconds) && _seconds > 0 ? _seconds * 1e3 : 6e3;
  }
  play() {
    if (this.timer !== null) return;
    this.timer = window.setInterval(() => {
      this.show((this.index + 1) % this.messages.length);
    }, this.intervalMs());
  }
  pause() {
    if (this.timer === null) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }
  show(next) {
    const _leaving = this.messages[this.index];
    const _entering = this.messages[next];
    if (_leaving) {
      _leaving.classList.remove("is-active");
      _leaving.classList.add("is-leaving");
      _leaving.setAttribute("aria-hidden", "true");
      this.settle(_leaving);
    }
    _entering?.classList.add("is-active");
    _entering?.removeAttribute("aria-hidden");
    this.index = next;
  }
  /**
   * Drops a message back below the floor once it is off the top. Removing the
   * class is what returns it to the transition-less waiting state, so it does
   * not animate backwards on the way down.
   */
  settle(message) {
    const _pending = this.settles.get(message);
    if (_pending !== void 0) window.clearTimeout(_pending);
    this.settles.set(
      message,
      window.setTimeout(() => {
        message.classList.remove("is-leaving");
        this.settles.delete(message);
      }, ROLL_MS)
    );
  }
  destroy() {
    if (this.isDestroyed()) return;
    this.pause();
    this.settles.forEach((timer) => window.clearTimeout(timer));
    this.settles.clear();
    super.destroy();
  }
}
useSectionLifecycle("promotion-bar", {
  onLoad: mountComponent(PromotionBar),
  onUnload: (_root, instance) => instance?.destroy()
});
const BUNDLE_SELECTOR = "[data-cart-bundle]";
const BUNDLE_FORM_SELECTOR = "[data-bundle-form]";
const BUNDLE_ERROR = "Those pieces could not be added to your bag.";
const PLATE_FADE_MS = TIMING.photoFade;
class CartShortcut extends BaseComponent {
  constructor() {
    super(...arguments);
    this.componentName = "CartShortcut";
    this.variants = [];
    this.color = null;
    this.size = null;
    this.plateTimer = null;
    this.touchedColor = null;
  }
  getDefaultConfig() {
    return { debug: false };
  }
  init() {
    this.elements = cacheElements(this.container, {
      variantId: "[data-variant-input]",
      price: "[data-price]",
      colorValue: "[data-color-value]",
      colors: "[data-colors]",
      sizes: "[data-sizes]",
      submit: '[data-action="cart-submit"]',
      data: "[data-variants]"
    });
    const _bundle = this.container.querySelector(BUNDLE_SELECTOR);
    if (_bundle) {
      this.initBundle(_bundle);
      return;
    }
    if (!guardElement(this.elements.variantId, "CartShortcut: variant input not found")) return;
    if (!guardElement(this.elements.data, "CartShortcut: variant data not found")) return;
    this.variants = this.readVariants(this.elements.data);
    if (this.variants.length === 0) return;
    this.color = this.checkedValue(this.elements.colors);
    this.size = this.checkedValue(this.elements.sizes);
    if (this.elements.colors) {
      this.addListener(this.elements.colors, "change", (event) => {
        this.color = event.target.value;
        this.sync();
      });
    }
    if (this.elements.sizes) {
      this.addListener(this.elements.sizes, "change", (event) => {
        this.size = event.target.value;
        this.sync();
      });
    }
  }
  readVariants(source) {
    try {
      const _parsed = JSON.parse(source.textContent ?? "[]");
      return Array.isArray(_parsed) ? _parsed : [];
    } catch (error) {
      consoleMessage("CartShortcut: variant data is not valid JSON", "error", error);
      return [];
    }
  }
  checkedValue(group) {
    return group?.querySelector("input:checked")?.value ?? null;
  }
  find(color, size) {
    return this.variants.find(
      (variant) => (color === null || variant.color === color) && (size === null || variant.size === size)
    ) ?? null;
  }
  sync() {
    this.retargetSizes();
    const _variant = this.find(this.color, this.size);
    if (this.elements.colorValue && this.color) {
      this.elements.colorValue.textContent = this.color;
    }
    if (_variant && this.elements.variantId instanceof HTMLInputElement) {
      this.elements.variantId.value = String(_variant.id);
    }
    if (_variant && this.elements.price) {
      this.elements.price.textContent = _variant.price;
      this.tickPrice(this.elements.price);
    }
    this.setSubmitState(_variant?.available === true);
    if (_variant) this.publishVariant(_variant.id);
    this.log(`variant ${_variant?.id ?? "none"} for ${this.color ?? "—"} / ${this.size ?? "—"}`);
  }
  /**
   * Keeps ?variant= in the address bar in step with the swatches.
   *
   * Without it a shopper who picks a colour and copies the URL, or reloads,
   * sends everyone back to the default colourway — the page they were looking
   * at had no address of its own. It is also what the colour collections rely
   * on to land correctly, so the two halves now agree.
   *
   * Only the product page's buy box does this. The same component drives the
   * bundle panel on the home and collection pages, and those are not a single
   * product — writing a variant into those URLs would be meaningless.
   *
   * replaceState rather than pushState: the back button should leave the
   * product, not walk back through every swatch that was tried.
   */
  publishVariant(id) {
    if (this.container.dataset.sectionType !== "product-buy-box") return;
    try {
      const _url = new URL(window.location.href);
      if (_url.searchParams.get("variant") === String(id)) return;
      _url.searchParams.set("variant", String(id));
      window.history.replaceState(window.history.state, "", _url);
    } catch (error) {
      consoleMessage("CartShortcut: could not publish the variant to the URL", "warn", error);
    }
  }
  /**
   * Re-points every size at the variant it means in the current colourway, and
   * moves off a size that colourway does not carry — leaving a disabled size
   * checked would submit a variant nobody can buy.
   */
  retargetSizes() {
    const _labels = this.elements.sizes?.querySelectorAll("[data-size]");
    if (!_labels || _labels.length === 0) return;
    let _fallback = null;
    let _currentIsAvailable = false;
    _labels.forEach((label) => {
      const _input = label.querySelector("input");
      if (!_input) return;
      const _variant = this.find(this.color, _input.value);
      const _available = _variant?.available === true;
      _input.dataset.variantId = _variant ? String(_variant.id) : "";
      _input.disabled = !_available;
      label.classList.toggle("is-sold-out", !_available);
      label.classList.toggle("is-selected", _input.checked && _available);
      if (_available && !_fallback) _fallback = _input;
      if (_input.checked && _available) _currentIsAvailable = true;
    });
    if (_currentIsAvailable || !_fallback) return;
    const _next = _fallback;
    _next.checked = true;
    _next.closest("[data-size]")?.classList.add("is-selected");
    this.size = _next.value;
  }
  /**
   * The bed, complete: one colourway, one size, every piece in the collection.
   *
   * `product-form-component` is deliberately not used — it submits a single
   * variant. The whole set goes to /cart/add.js in one request, asking for the
   * drawer's section back so the re-render has something to morph.
   */
  initBundle(bundle) {
    const _form = bundle.querySelector(BUNDLE_FORM_SELECTOR);
    if (!guardElement(_form, "CartShortcut: bundle form not found")) return;
    this.syncBundle(bundle);
    this.addListener(_form, "change", (event) => {
      const _target = event.target;
      if (!(_target instanceof HTMLInputElement)) return;
      const _piece = _target.closest("[data-piece]");
      if (!_piece) return;
      if (_target.closest("[data-piece-colors]")) this.touchedColor = _target.value;
      this.syncPiece(_piece);
      this.syncBundle(bundle);
    });
    this.addListener(_form, "submit", (event) => {
      event.preventDefault();
      void this.submitBundle(bundle, _form);
    });
    this.log("bundle ready");
  }
  /** The variants of one piece, as its row ships them. */
  pieceVariants(piece) {
    const _source = piece.querySelector("[data-piece-variants]");
    if (!_source?.textContent) return [];
    try {
      return JSON.parse(_source.textContent);
    } catch (error) {
      consoleMessage("CartShortcut: piece variants are not valid JSON", "error", error);
      return [];
    }
  }
  /**
   * Re-prices one row against its own colour and size, and keeps the hidden
   * input — the thing the no-script form posts — pointing at the same variant
   * the shopper can see.
   *
   * Sizes are enabled per colour rather than per piece: a colourway can be sold
   * out in King while another has it, and a radio the shopper cannot buy should
   * say so before the bag does.
   */
  syncPiece(piece) {
    const _variants = this.pieceVariants(piece);
    if (_variants.length === 0) return;
    const _color = this.checkedValue(piece.querySelector("[data-piece-colors]"));
    const _sizes = piece.querySelector("[data-piece-sizes]");
    _sizes?.querySelectorAll("input").forEach((input) => {
      const _sellable = _variants.some(
        (variant) => variant.size === input.value && (_color === null || variant.color === _color) && variant.available
      );
      input.disabled = !_sellable;
      input.closest("label")?.classList.toggle("is-sold-out", !_sellable);
    });
    const _checked = _sizes?.querySelector("input:checked");
    if (!_checked || _checked.disabled) {
      const _first = _sizes?.querySelector("input:not([disabled])");
      if (_first) _first.checked = true;
    }
    const _size = this.checkedValue(_sizes ?? null);
    const _variant = _variants.find(
      (candidate) => (_color === null || candidate.color === _color) && (_size === null || candidate.size === _size)
    );
    const _price = piece.querySelector("[data-piece-price]");
    if (_price && _variant) {
      _price.textContent = _variant.price;
      this.tickPrice(_price);
    }
    const _input = piece.querySelector("[data-piece-input]");
    if (_input && _variant) _input.value = String(_variant.id);
    piece.dataset.cents = String(_variant?.cents ?? 0);
    piece.dataset.sellable = String(_variant?.available === true);
  }
  /**
   * The incoming photograph fades in over the outgoing one, which stays
   * underneath at full strength until it has finished. Fading through the page
   * ground instead would dim both beds at once.
   */
  fadePlate() {
    const _plates = this.container.querySelector("[data-bundle-plates]");
    if (!_plates || !this.color) return;
    const _next = _plates.querySelector(`[data-colorway="${CSS.escape(this.color)}"]`);
    const _current = _plates.querySelector(".is-current");
    if (!_next || _next === _current) return;
    if (this.plateTimer !== null) window.clearTimeout(this.plateTimer);
    _plates.querySelectorAll(".is-under").forEach((plate) => plate.classList.remove("is-under"));
    if (_current) {
      _current.classList.remove("is-current");
      _current.classList.add("is-under");
    }
    _next.classList.add("is-current");
    this.plateTimer = window.setTimeout(() => {
      _current?.classList.remove("is-under");
      this.plateTimer = null;
    }, PLATE_FADE_MS);
  }
  /**
   * The panel's total is the sum of the rows, and the button is only live while
   * every piece can actually be bought — the offer is all three or nothing.
   */
  syncBundle(bundle) {
    const _pieces = Array.from(bundle.querySelectorAll("[data-piece]"));
    _pieces.forEach((piece) => {
      if (piece.dataset.cents === void 0) this.syncPiece(piece);
    });
    const _total = _pieces.reduce((sum, piece) => sum + Number(piece.dataset.cents ?? 0), 0);
    const _sellable = _pieces.length > 0 && _pieces.every((piece) => piece.dataset.sellable === "true");
    const _price = bundle.querySelector("[data-price]");
    if (_price) {
      const _percent = Number(_price.dataset.discountPercent ?? 0);
      const _due = _percent > 0 ? Math.round(_total * (100 - _percent) / 100) : _total;
      _price.textContent = formatPrice(_due);
      this.tickPrice(_price);
      const _full = bundle.querySelector("[data-price-full]");
      if (_full) _full.textContent = formatPrice(_total);
    }
    this.color = this.touchedColor ?? this.checkedValue(_pieces[0]?.querySelector("[data-piece-colors]") ?? null);
    this.fadePlate();
    this.setSubmitState(_sellable);
  }
  bundleIds(bundle) {
    return Array.from(bundle.querySelectorAll("[data-piece-input]")).map((input) => input.value).filter(Boolean);
  }
  /**
   * The identity the set's lines carry into the cart, so it can draw them as one
   * group rather than as three unrelated lines.
   *
   * The key has to be stable across adds: adding the same set twice should raise
   * the quantity on the lines already there, which is what Shopify does when the
   * variant and the properties both match. It therefore carries the colourway
   * rather than anything per-request — Woods and Mesa are two sets, the same set
   * twice is one.
   *
   * The colourway is only part of it when every piece shares one. A shopper can
   * pick a colour per row here, and "The bed, complete · Woods" would be a lie
   * about a set with a Mesa pillowcase in it.
   */
  bundleSet(bundle) {
    const _label = bundle.dataset.setLabel?.trim() || "Set";
    const _colors = Array.from(bundle.querySelectorAll("[data-piece-colors]")).map(
      (group) => this.checkedValue(group)
    );
    const _shared = _colors.length > 0 && _colors.every((color) => color && color === _colors[0]) ? _colors[0] : null;
    const _full = _shared ? `${_label} · ${_shared}` : _label;
    return {
      key: _full.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, ""),
      label: _full
    };
  }
  async submitBundle(bundle, form) {
    const _ids = this.bundleIds(bundle);
    if (_ids.length === 0) return;
    const _set = this.bundleSet(bundle);
    let _resolve = () => {
    };
    const _promise = new Promise((resolve) => {
      _resolve = resolve;
    });
    bundle.dispatchEvent(new CartLinesUpdateSignal(_promise, _ids.length));
    const _fail = (message) => {
      bundle.dispatchEvent(new CartErrorSignal(message));
      _resolve({ cart: null, detail: { didError: true, source: "refuge-cart-shortcut" } });
    };
    try {
      const _response = await fetch(`${form.action}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          items: _ids.map((id) => ({
            id: Number(id),
            quantity: 1,
            properties: { _set: _set.key, _set_label: _set.label }
          })),
          sections: CART_DRAWER_SECTION,
          sections_url: window.location.pathname
        })
      });
      const _payload = await _response.json();
      if (!_response.ok) {
        _fail(_payload.description || _payload.message || BUNDLE_ERROR);
        return;
      }
      _resolve({
        cart: null,
        detail: {
          sections: _payload.sections,
          itemCount: this.readCount(_payload.sections?.[CART_DRAWER_SECTION]),
          source: "refuge-cart-shortcut",
          didError: false
        }
      });
    } catch (error) {
      consoleMessage("CartShortcut: bundle add failed", "error", error);
      _fail(BUNDLE_ERROR);
    }
  }
  /** The drawer's own hidden count, read out of the section it just returned. */
  readCount(section) {
    if (!section) return void 0;
    const _rendered = new DOMParser().parseFromString(section, "text/html").querySelector('[ref="cartItemCount"]')?.textContent?.trim();
    const _count = _rendered ? Number.parseInt(_rendered, 10) : Number.NaN;
    return Number.isNaN(_count) ? void 0 : _count;
  }
  /**
   * The price is the one number a click changes, so it re-enters rather than
   * swapping in place. The class has to come off and go back on for the
   * animation to run a second time, and reading offsetWidth between the two is
   * what forces the style change to land — without it the browser coalesces
   * both into one frame and nothing plays.
   */
  tickPrice(price) {
    if (!price) return;
    price.classList.remove("is-ticking");
    void price.offsetWidth;
    price.classList.add("is-ticking");
  }
  /**
   * The fade's timer is the one thing here that outlives a teardown — the
   * listeners go with addListener, and the theme editor re-renders this section
   * on every setting change.
   */
  destroy() {
    if (this.isDestroyed()) return;
    if (this.plateTimer !== null) {
      window.clearTimeout(this.plateTimer);
      this.plateTimer = null;
    }
    super.destroy();
  }
  setSubmitState(available) {
    const _submit = this.elements.submit;
    if (!(_submit instanceof HTMLButtonElement)) return;
    const _label = _submit.querySelector("[data-submit-text]") ?? _submit.querySelector(".refuge-button__label");
    const _text = available ? this.container.dataset.ctaLabel : this.container.dataset.soldOutLabel;
    _submit.disabled = !available;
    _submit.classList.toggle("refuge-button--sold-out", !available);
    if (_label && _text) _label.textContent = _text;
  }
}
const LIFECYCLE = {
  mount: "visible",
  onLoad: mountComponent(CartShortcut),
  onUnload: (_root, instance) => instance?.destroy()
};
useSectionLifecycle("cart-shortcut", LIFECYCLE);
useSectionLifecycle("quick-shop", LIFECYCLE);
useSectionLifecycle("product-buy-box", LIFECYCLE);
const CART_LINES_UPDATE = "shopify:cart:lines-update";
const COUNT_RECONCILE_MS = 400;
const TOGGLE_SELECTOR = "[data-cart-toggle]";
const HEADER_SHELL_SELECTOR = ".refuge-header-shell";
const PAGE_WRAPPER_SELECTOR = ".page-wrapper";
const SECTION_ID = "refuge-cart-drawer";
const PENDING_SELECTOR = "[data-cart-drawer-pending]";
const HYDRATION_SELECTOR = '[data-hydration-key="refuge-cart-drawer-inner"]';
const FAILURE_TEMPLATE = "#refuge-cart-drawer-failure";
const RETRY_SELECTOR = '[data-action="cart-retry"]';
const AUTO_DISMISS_MS = 5e3;
const OPEN_AFTER_ADD_MS = 400;
const CartDrawerEvents = {
  open: "cart:open",
  close: "cart:close",
  updated: "cart:updated"
};
class RefugeCartDrawer extends BaseComponent {
  constructor() {
    super(...arguments);
    this.componentName = "RefugeCartDrawer";
    this.dialog = null;
    this.scroller = null;
    this.openTimer = null;
    this.dismissTimer = null;
    this.scrollLocked = false;
    this.hydration = null;
  }
  getDefaultConfig() {
    return { debug: false, autoOpen: true };
  }
  init() {
    this.elements = cacheElements(this.container, { dialog: "[data-cart-drawer]" });
    if (!guardElement(this.elements.dialog, "RefugeCartDrawer: dialog not found")) return;
    this.dialog = this.elements.dialog;
    this.addListener(document, "click", (event) => this.handleDocumentClick(event));
    this.addListener(this.dialog, "close", () => this.handleClose());
    this.addListener(window, "resize", useDebounce(() => this.dialog?.open && this.anchor(), 150));
    this.addListener(document, "pointerover", (event) => this.handleIntent(event));
    this.addListener(document, "focusin", (event) => this.handleIntent(event));
    this.addListener(document, CART_LINES_UPDATE, (event) => this.handleCartLinesUpdate(event));
    for (const signal of ["pointerenter", "focusin", "keydown", "click"]) {
      this.addListener(this.dialog, signal, () => this.cancelAutoDismiss());
    }
    this.publishApi();
  }
  handleDocumentClick(event) {
    const _target = event.target;
    if (!_target) return;
    if (_target.closest(TOGGLE_SELECTOR)) {
      event.preventDefault();
      this.toggle();
      return;
    }
    if (_target.closest(RETRY_SELECTOR)) {
      event.preventDefault();
      void this.hydrate();
      return;
    }
    if (!this.dialog?.open) return;
    if (_target === this.dialog || _target.closest('[data-action="cart-close"]')) {
      this.close();
    }
  }
  open() {
    if (!this.dialog || this.dialog.open) return;
    this.anchor();
    this.dialog.showModal();
    void this.hydrate();
    this.lockScroll(true);
    this.setToggleState(true);
    this.announce(CartDrawerEvents.open);
    this.log("opened");
  }
  close() {
    if (!this.dialog?.open) return;
    this.dialog.close();
  }
  /** Opened by an add rather than by the shopper, so it sees itself out. */
  scheduleAutoDismiss() {
    this.cancelAutoDismiss();
    window.requestAnimationFrame(() => {
      if (!this.dialog?.open) return;
      this.dismissTimer = window.setTimeout(() => {
        this.dismissTimer = null;
        this.close();
      }, AUTO_DISMISS_MS);
    });
  }
  cancelAutoDismiss() {
    if (this.dismissTimer === null) return;
    window.clearTimeout(this.dismissTimer);
    this.dismissTimer = null;
  }
  toggle() {
    if (this.dialog?.open) {
      this.close();
      return;
    }
    this.open();
  }
  handleClose() {
    this.cancelAutoDismiss();
    this.lockScroll(false);
    this.setToggleState(false);
    this.announce(CartDrawerEvents.close);
    this.log("closed");
  }
  handleCartLinesUpdate(event) {
    const _shouldOpen = this.config.autoOpen && event.action === "add" && !this.dialog?.open;
    this.predictCount(event);
    const _sourceModal = event.target instanceof Element ? event.target.closest("dialog:modal") : null;
    event.promise?.then(({ detail }) => {
      if (detail?.didError) return;
      this.syncCount(detail?.itemCount);
      void this.reconcileCount();
      if (!_shouldOpen) return;
      this.openAfterFeedback(_sourceModal);
    }).catch((error) => {
      consoleMessage("RefugeCartDrawer: cart update rejected", "error", error);
    });
  }
  /**
   * The bag opens once the add has succeeded. Opening on dispatch, as this used
   * to, threw the drawer up before the cart had answered — and threw it up on a
   * failure too. The wait is now its own number rather than the button's.
   */
  openAfterFeedback(sourceModal) {
    if (this.openTimer !== null) window.clearTimeout(this.openTimer);
    this.openTimer = window.setTimeout(() => {
      this.openTimer = null;
      if (sourceModal?.open) {
        sourceModal.addEventListener(
          "close",
          () => {
            this.open();
            this.scheduleAutoDismiss();
          },
          { once: true }
        );
        return;
      }
      this.open();
      this.scheduleAutoDismiss();
    }, OPEN_AFTER_ADD_MS);
  }
  /**
   * Moves the count by what is being added, before the cart has answered.
   *
   * The bundle sends its own line count on the event, since it adds three at
   * once through fetch rather than through a form the DOM can be asked about.
   *
   * Only for adds. A removal or a quantity change is driven from inside the
   * drawer, where the line is already on screen and its own component redraws
   * it — guessing there would fight that.
   */
  predictCount(event) {
    if (event.action !== "add") return;
    const _form = event.target instanceof Element ? event.target.closest("form") : null;
    const _quantity = Number(_form?.querySelector('input[name="quantity"]')?.value);
    const _fallback = Math.max(1, Number.isFinite(_quantity) ? _quantity : 1);
    const _added = event.addedLines ?? _fallback;
    const _shown = Number(
      document.querySelector("[data-cart-count]")?.textContent?.trim()
    );
    const _base = Number.isFinite(_shown) ? _shown : window.STOREFRONT?.cart?.count ?? 0;
    this.syncCount(_base + _added);
  }
  /**
   * Writes the cart's real count after everything else has settled.
   *
   * Deliberately late and deliberately cheap: /cart.js renders nothing, and the
   * delay is there to land after Horizon's re-render rather than before it.
   * Failure is silent — the optimistic number is already on screen, and being
   * briefly stale beats replacing it with an error.
   */
  async reconcileCount() {
    await new Promise((resolve) => window.setTimeout(resolve, COUNT_RECONCILE_MS));
    if (this.isDestroyed()) return;
    try {
      const _response = await fetch("/cart.js", { headers: { Accept: "application/json" } });
      if (!_response.ok) return;
      const _cart = await _response.json();
      this.syncCount(_cart.item_count);
    } catch (error) {
      consoleMessage("RefugeCartDrawer: could not reconcile the cart count", "warn", error);
    }
  }
  /** The bag count sits outside the section the drawer re-renders through. */
  syncCount(itemCount) {
    const _rendered = this.container.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
    const _count = itemCount ?? (_rendered ? Number.parseInt(_rendered, 10) : Number.NaN);
    if (Number.isNaN(_count)) return;
    window.STOREFRONT.cart.count = _count;
    document.querySelectorAll("[data-cart-count]").forEach((element) => {
      element.textContent = String(_count);
      if (element.dataset.cartCount === "badge") element.toggleAttribute("hidden", _count === 0);
    });
    document.querySelectorAll("[data-cart-count-group]").forEach((element) => element.toggleAttribute("hidden", _count === 0));
    this.announce(CartDrawerEvents.updated, { count: _count });
  }
  setToggleState(open) {
    document.querySelectorAll(TOGGLE_SELECTOR).forEach((toggle) => {
      toggle.setAttribute("aria-expanded", String(open));
    });
  }
  /** Reaching for the bag is enough to pay for the lines before they are wanted. */
  handleIntent(event) {
    if (this.hydration) return;
    const _target = event.target;
    if (!_target?.closest?.(TOGGLE_SELECTOR)) return;
    void this.hydrate();
  }
  /**
   * The lines are not in the page. layout/theme.liquid renders the panel with an
   * empty keyed placeholder, and this fetches the real ones from the Section
   * Rendering API — 7KB per line that every page was carrying whether or not
   * the shopper ever opened the bag.
   *
   * Horizon's cart-update morph fills the same placeholder, so a mutation can
   * beat this to it. Single-flight, and a no-op once the placeholder is gone.
   */
  hydrate() {
    if (this.hydration) return this.hydration;
    const _pending = this.container.querySelector(PENDING_SELECTOR);
    if (!_pending) return Promise.resolve();
    _pending.setAttribute("aria-busy", "true");
    _pending.replaceChildren();
    this.hydration = this.renderLines().catch((error) => {
      consoleMessage("RefugeCartDrawer: hydration failed", "error", error);
      this.hydration = null;
      this.showFailure();
    });
    return this.hydration;
  }
  /**
   * A failed hydration used to leave the panel showing its heading and nothing
   * else, still marked aria-busy: no notice, no retry, and no way through to
   * the cart page. The placeholder keeps its pending flag, so reopening still
   * retries on its own and Retry is only the faster way to the same call.
   */
  showFailure() {
    const _pending = this.container.querySelector(PENDING_SELECTOR);
    const _template = document.querySelector(FAILURE_TEMPLATE);
    if (!_pending || !_template) return;
    _pending.removeAttribute("aria-busy");
    _pending.replaceChildren(_template.content.cloneNode(true));
  }
  async renderLines() {
    const _url = new URL(window.location.href);
    _url.searchParams.set("section_id", SECTION_ID);
    const _response = await fetch(_url.toString(), { cache: "no-store" });
    if (!_response.ok) throw new Error(`${SECTION_ID} answered ${_response.status}`);
    const _rendered = new DOMParser().parseFromString(await _response.text(), "text/html").querySelector(HYDRATION_SELECTOR);
    if (!_rendered) throw new Error(`${SECTION_ID} carried no ${HYDRATION_SELECTOR}`);
    const _pending = this.container.querySelector(PENDING_SELECTOR);
    if (!_pending) return;
    _pending.replaceChildren(...Array.from(document.importNode(_rendered, true).childNodes));
    _pending.classList.remove("refuge-cart-drawer__inner--pending");
    _pending.removeAttribute("data-cart-drawer-pending");
    _pending.removeAttribute("aria-busy");
    this.log("lines hydrated");
  }
  /**
   * The panel hangs off the header, not off the header group: once the group's
   * sticky offset has pulled the promotion bar out of view, the group's height
   * and the header's bottom edge are 58px apart. Measured at open rather than
   * published from header.ts, because the edge moves with scroll and nothing
   * here should run per frame.
   */
  anchor() {
    const _shell = document.querySelector(HEADER_SHELL_SELECTOR);
    if (!_shell || !this.dialog) return;
    const _bottom = Math.max(0, Math.round(_shell.getBoundingClientRect().bottom));
    this.dialog.style.setProperty("--refuge-cart-drawer-anchor", `${_bottom}px`);
  }
  /**
   * A modal dialog makes the page inert but does not stop it scrolling.
   *
   * Which element to stop is not the root everywhere: from 990 Horizon's
   * base.css pins the document at 100dvh and hands scrolling to .page-wrapper,
   * so writing overflow onto <html> there stops nothing. `scroll-lock` is
   * Horizon's own hook for exactly this and covers both. The gutter replaces
   * whichever scrollbar is being removed, so the layout behind stays still.
   */
  lockScroll(locked) {
    if (locked === this.scrollLocked) return;
    this.scrollLocked = locked;
    if (!locked) {
      document.documentElement.removeAttribute("scroll-lock");
      this.scroller?.style.removeProperty("padding-inline-end");
      this.scroller = null;
      return;
    }
    const _wrapper = document.querySelector(PAGE_WRAPPER_SELECTOR);
    const _scrolls = _wrapper != null && _wrapper.scrollHeight > _wrapper.clientHeight;
    this.scroller = _scrolls ? _wrapper : document.documentElement;
    const _gutter = _scrolls ? this.scroller.offsetWidth - this.scroller.clientWidth : window.innerWidth - this.scroller.clientWidth;
    document.documentElement.setAttribute("scroll-lock", "");
    if (_gutter > 0) this.scroller.style.setProperty("padding-inline-end", `${_gutter}px`);
  }
  announce(name, detail = {}) {
    window.STOREFRONT.events.dispatchEvent(new CustomEvent(name, { detail }));
    document.dispatchEvent(new CustomEvent(`refuge:${name}`, { detail, bubbles: true }));
  }
  publishApi() {
    window.STOREFRONT.cart.open = () => this.open();
    window.STOREFRONT.cart.close = () => this.close();
    window.STOREFRONT.cart.toggle = () => this.toggle();
  }
  destroy() {
    if (this.isDestroyed()) return;
    if (this.openTimer !== null) window.clearTimeout(this.openTimer);
    this.cancelAutoDismiss();
    this.lockScroll(false);
    delete window.STOREFRONT.cart.open;
    delete window.STOREFRONT.cart.close;
    delete window.STOREFRONT.cart.toggle;
    super.destroy();
  }
}
useSectionLifecycle("cart-drawer", {
  onLoad: mountComponent(RefugeCartDrawer),
  onUnload: (_root, instance) => instance?.destroy()
});
const REDUCED_MOTION$1 = "(prefers-reduced-motion: reduce)";
const EXIT_DURATION = 150;
const DAY = 864e5;
function hasStoredConsent(privacy) {
  try {
    return Object.values(privacy.currentVisitorConsent()).some((value) => value !== "");
  } catch (error) {
    consoleMessage("CookieBar: could not read visitor consent", "warn", error);
    return false;
  }
}
class CookieBar extends BaseComponent {
  constructor() {
    super(...arguments);
    this.componentName = "CookieBar";
    this.privacy = null;
    this.frame = null;
    this.exitTimer = null;
  }
  getDefaultConfig() {
    return {
      debug: false,
      storageKey: "refuge:cookie-consent",
      showPolicy: "always",
      consentDays: 365
    };
  }
  init() {
    this.elements = cacheElements(this.container, {
      accept: '[data-action="cookie-accept"]',
      reject: '[data-action="cookie-reject"]',
      close: '[data-action="cookie-close"]'
    });
    if (!guardElement(this.elements.accept, "CookieBar: accept button not found")) return;
    if (hasGlobalPrivacyControl()) {
      this.log("Global Privacy Control detected — an accept will not ask for sale of data");
    }
    this.addListener(this.elements.accept, "click", () => this.choose("accepted"));
    if (this.elements.reject) {
      this.addListener(this.elements.reject, "click", () => this.choose("rejected"));
    }
    if (this.elements.close) {
      this.addListener(this.elements.close, "click", () => this.choose("rejected"));
    }
    this.addListener(window.STOREFRONT.events, CONSENT_REOPEN, () => this.reopen());
    void this.reveal();
  }
  /** Puts the bar back up after a choice, so it can be made again. */
  reopen() {
    if (this.exitTimer !== null) {
      window.clearTimeout(this.exitTimer);
      this.exitTimer = null;
    }
    this.container.classList.remove("is-leaving");
    if (!window.Shopify?.designMode) this.remember("pending");
    this.show();
    this.log("reopened from a privacy control");
  }
  async reveal() {
    this.privacy = await loadCustomerPrivacy();
    if (this.isDestroyed()) return;
    const _record = this.storedRecord();
    const _answer = _record !== null && _record.choice !== "pending" ? _record : null;
    const _live = _answer !== null && !this.hasExpired(_answer);
    if (_live) this.replay(_answer);
    if (window.Shopify?.designMode) {
      this.log("theme editor — showing only while the section is selected");
      return;
    }
    if (!this.shouldShow(_record, _live)) {
      this.log(_live ? "choice still current — staying hidden" : "no consent needed here");
      return;
    }
    this.remember("pending");
    this.show();
  }
  /**
   * `record` is null when nothing is stored, `pending` when this visitor has
   * been asked and not answered, and a choice only when that choice has
   * expired — a current one never gets this far.
   */
  shouldShow(record, live) {
    if (live) return false;
    if (record?.choice === "pending") return true;
    if (this.config.showPolicy === "always") return true;
    if (!this.privacy) return true;
    try {
      return this.privacy.shouldShowBanner() || record !== null && hasStoredConsent(this.privacy);
    } catch (error) {
      consoleMessage("CookieBar: could not read banner state", "warn", error);
      return true;
    }
  }
  hasExpired(record) {
    const _days = Number(this.config.consentDays);
    if (!Number.isFinite(_days) || _days <= 0) return false;
    return Date.now() - record.at >= _days * DAY;
  }
  storedRecord() {
    let _raw = null;
    try {
      _raw = window.localStorage.getItem(this.config.storageKey);
    } catch {
      return null;
    }
    if (!_raw) return null;
    try {
      const _parsed = JSON.parse(_raw);
      if (!_parsed || typeof _parsed !== "object") return null;
      const { choice, at } = _parsed;
      if (choice !== "accepted" && choice !== "rejected" && choice !== "pending") return null;
      if (typeof at !== "number" || !Number.isFinite(at)) return null;
      return { choice, at };
    } catch {
      return null;
    }
  }
  remember(choice) {
    try {
      window.localStorage.setItem(
        this.config.storageKey,
        JSON.stringify({ choice, at: Date.now() })
      );
    } catch {
    }
  }
  /** Puts a still-current choice back into an API that has lost it. */
  replay(record) {
    if (!this.privacy || hasStoredConsent(this.privacy)) return;
    this.log(`replaying stored ${record.choice} into the consent API`);
    this.record(consentFor(record.choice === "accepted"));
  }
  show() {
    this.container.hidden = false;
    this.frame = window.requestAnimationFrame(() => {
      this.frame = window.requestAnimationFrame(() => {
        this.container.classList.add("is-revealed");
      });
    });
  }
  /** The theme editor's preview of this section, raised and dropped by selection. */
  preview(open) {
    if (open) {
      this.show();
      return;
    }
    this.conceal();
  }
  choose(choice) {
    const _consent = consentFor(choice === "accepted");
    this.conceal();
    if (!window.Shopify?.designMode) {
      this.remember(choice);
      this.record(_consent);
    }
    window.STOREFRONT?.events?.dispatchEvent(
      new CustomEvent("cookie:choice", { detail: { choice, consent: _consent } })
    );
    this.log(`recorded ${choice}`);
  }
  conceal() {
    this.container.classList.remove("is-revealed");
    if (window.matchMedia(REDUCED_MOTION$1).matches) {
      this.container.hidden = true;
      return;
    }
    this.container.classList.add("is-leaving");
    this.exitTimer = window.setTimeout(() => {
      this.container.hidden = true;
      this.container.classList.remove("is-leaving");
    }, EXIT_DURATION);
  }
  record(consent) {
    if (!this.privacy) {
      this.log("consent tracking API unavailable — stored locally only", "warn");
      return;
    }
    try {
      this.privacy.setTrackingConsent(consent, (response) => {
        if (response?.error) {
          consoleMessage("CookieBar: Shopify rejected the consent", "error", response.error);
        }
      });
    } catch (error) {
      consoleMessage("CookieBar: could not write consent", "error", error);
    }
  }
  destroy() {
    if (this.frame !== null) window.cancelAnimationFrame(this.frame);
    if (this.exitTimer !== null) window.clearTimeout(this.exitTimer);
    super.destroy();
  }
}
useSectionLifecycle("cookie-bar", {
  // Loading Shopify's consent feature is a script fetch, and the bar is not part
  // of first paint — so it waits for idle. whenIdle() caps that at 2s, and the
  // feature load caps at 2s more, which bounds the worst case at ~4s.
  mount: "idle",
  onLoad: mountComponent(CookieBar),
  onUnload: (_root, instance) => instance?.destroy(),
  onSelect: (_root, instance) => instance?.preview(true),
  onDeselect: (_root, instance) => instance?.preview(false)
});
const PREDICTIVE_SECTION = "refuge-predictive-search";
const PRODUCT_PREVIEW = "refuge-shop-menu__preview--product";
const HOVER_QUERY = "(hover: hover) and (pointer: fine) and (min-width: 1024px)";
const COMPACT_QUERY = "(max-width: 767.98px)";
const HOVER_CLOSE_DELAY = 160;
const RESULTS_ID = "refuge-predictive-search-results";
const MIN_QUERY_LENGTH = 2;
const QUERY_DEBOUNCE = 250;
class RefugeHeader extends BaseComponent {
  constructor() {
    super(...arguments);
    this.componentName = "RefugeHeader";
    this.observer = null;
    this.groupResize = null;
    this.openPanel = null;
    this.defaultPreview = "";
    this.defaultCaption = "";
    this.previewsWarmed = false;
    this.searchRequest = null;
    this.hoverTimer = null;
    this.lastQuery = "";
    this.compact = window.matchMedia(COMPACT_QUERY);
    this.search = useDebounce((term) => void this.fetchResults(term), QUERY_DEBOUNCE);
  }
  getDefaultConfig() {
    return { debug: false, scrolledOffset: 1 };
  }
  init() {
    this.elements = cacheElements(this.container, {
      header: "[data-header]",
      sentinel: "[data-header-sentinel]",
      shell: ".refuge-header-shell--sticky",
      toggle: "[data-header-shop]",
      menu: "[data-shop-menu]",
      scrim: "[data-header-scrim]",
      preview: "[data-shop-preview]",
      caption: "[data-shop-caption]",
      searchToggle: "[data-header-search]",
      searchPanel: "[data-search-panel]",
      searchInput: "[data-search-input]",
      searchResults: "[data-search-results]",
      searchStatus: "[data-search-status]"
    });
    if (!guardElement(this.elements.header, "RefugeHeader: header not found")) return;
    this.pinGroup();
    this.watchScroll();
    this.wireDisclosure();
    this.wireSearch();
    this.wirePreview();
  }
  /**
   * Publishes two measurements taken from Shopify's header group: how far the
   * header sits below its top, which is what the group's sticky offset pulls
   * up by, and the group's own height, which a full-viewport section subtracts
   * to fill what is left of the screen.
   *
   * Measured rather than assumed, because whatever sits above the header — a
   * promotion bar, and how many lines its message wraps to — is the merchant's
   * to change. The height goes on the root because its readers are elsewhere
   * in the document.
   */
  pinGroup() {
    const _group = this.container.closest("#header-group");
    if (!_group) return;
    const _shell = this.elements.shell;
    const _sync = () => {
      const _box = _group.getBoundingClientRect();
      document.documentElement.style.setProperty("--refuge-header-height", `${Math.round(_box.height)}px`);
      if (!_shell) return;
      const _offset = _shell.getBoundingClientRect().top - _box.top;
      _group.style.setProperty("--refuge-header-offset", `${Math.max(0, Math.round(_offset))}px`);
    };
    _sync();
    this.groupResize = new ResizeObserver(_sync);
    this.groupResize.observe(_group);
  }
  watchScroll() {
    const _sentinel = this.elements.sentinel;
    if (!_sentinel) return;
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.elements.header?.classList.toggle("is-scrolled", !entry.isIntersecting);
      },
      { rootMargin: `-${this.config.scrolledOffset}px 0px 0px 0px` }
    );
    this.observer.observe(_sentinel);
  }
  wireDisclosure() {
    const _toggle = this.elements.toggle;
    if (_toggle && this.elements.menu) {
      this.addListener(_toggle, "click", () => this.setPanel(this.openPanel === "menu" ? null : "menu"));
      this.wireHover(_toggle, this.elements.menu);
    }
    if (this.elements.scrim) {
      this.addListener(this.elements.scrim, "click", () => this.setPanel(null));
    }
    this.addListener(this.compact, "change", () => this.setPanel(this.openPanel));
    this.addListener(document, "keydown", (event) => {
      if (event.key !== "Escape" || !this.openPanel) return;
      const _returnTo = this.openPanel === "menu" ? this.elements.toggle : this.elements.searchToggle;
      this.setPanel(null);
      _returnTo?.focus();
    });
    this.addListener(document, "click", (event) => {
      if (!this.openPanel) return;
      if (this.container.contains(event.target)) return;
      this.setPanel(null);
    });
  }
  /**
   * Opens the mega nav on hover, and closes it once the pointer has left both
   * the toggle and the panel. The toggle stays a real button — click, Enter and
   * Escape work exactly as they did, which is what keyboard and touch use.
   */
  wireHover(toggle, menu) {
    const _hover = window.matchMedia(HOVER_QUERY);
    const _enter = () => {
      if (!_hover.matches) return;
      this.cancelHoverClose();
      if (this.openPanel !== "menu") this.setPanel("menu");
    };
    const _leave = () => {
      if (!_hover.matches || this.openPanel !== "menu") return;
      this.cancelHoverClose();
      this.hoverTimer = window.setTimeout(() => {
        this.hoverTimer = null;
        if (this.openPanel === "menu") this.setPanel(null);
      }, HOVER_CLOSE_DELAY);
    };
    this.addListener(toggle, "pointerenter", _enter);
    this.addListener(toggle, "pointerleave", _leave);
    this.addListener(menu, "pointerenter", () => this.cancelHoverClose());
    this.addListener(menu, "pointerleave", _leave);
  }
  cancelHoverClose() {
    if (this.hoverTimer === null) return;
    window.clearTimeout(this.hoverTimer);
    this.hoverTimer = null;
  }
  setPanel(next) {
    if (next !== "menu") this.cancelHoverClose();
    this.openPanel = next;
    this.applyPanel(this.elements.menu, this.elements.toggle, next === "menu");
    this.applyPanel(
      this.elements.searchPanel,
      this.elements.searchToggle,
      next === "search" || next === "menu" && this.compact.matches
    );
    if (this.elements.scrim) this.elements.scrim.hidden = next !== "menu";
    if (next === "menu") this.warmPreviews();
    this.elements.header?.classList.toggle("is-menu-open", next !== null);
    if (next === "search") this.elements.searchInput?.focus();
    this.log(`panel ${next ?? "closed"}`);
  }
  applyPanel(panel, toggle, open) {
    if (panel) panel.hidden = !open;
    toggle?.setAttribute("aria-expanded", String(open));
  }
  wireSearch() {
    const _toggle = this.elements.searchToggle;
    const _input = this.elements.searchInput;
    if (!_toggle || !_input) return;
    this.addListener(_toggle, "click", () => this.setPanel(this.openPanel === "search" ? null : "search"));
    this.addListener(_input, "input", () => {
      const _term = _input.value.trim();
      if (_term.length < MIN_QUERY_LENGTH) {
        this.search.cancel();
        this.clearResults();
        return;
      }
      this.search(_term);
    });
  }
  clearResults() {
    this.searchRequest?.abort();
    this.searchRequest = null;
    this.lastQuery = "";
    if (this.elements.searchResults) this.elements.searchResults.innerHTML = "";
    this.announce("");
  }
  /**
   * The rendered fragment carries the count the design draws, so the summary a
   * screen reader hears is the same sentence a sighted shopper reads rather
   * than a second one written here.
   */
  announce(summary) {
    if (this.elements.searchStatus) this.elements.searchStatus.textContent = summary;
  }
  /**
   * Results come back as rendered Liquid from Shopify's section renderer rather
   * than JSON, so prices, images and translations are the theme's own rather
   * than rebuilt here.
   */
  async fetchResults(term) {
    const _panel = this.elements.searchPanel;
    const _results = this.elements.searchResults;
    if (!_panel || !_results || term === this.lastQuery) return;
    const _base = _panel.dataset.predictiveUrl || "/search/suggest";
    this.searchRequest?.abort();
    this.searchRequest = new AbortController();
    try {
      const _url = new URL(_base, window.location.origin);
      _url.searchParams.set("q", term);
      _url.searchParams.set("resources[limit_scope]", "each");
      _url.searchParams.set("section_id", PREDICTIVE_SECTION);
      const _response = await fetch(_url.toString(), { signal: this.searchRequest.signal });
      if (!_response.ok) return;
      const _markup = await _response.text();
      const _fragment = new DOMParser().parseFromString(_markup, "text/html");
      const _rendered = _fragment.getElementById(RESULTS_ID);
      if (!_rendered) return;
      _results.replaceChildren(...Array.from(_rendered.childNodes));
      this.lastQuery = term;
      this.announce(_results.querySelector("[data-search-count]")?.textContent?.trim() ?? "");
      this.log(`search "${term}" rendered`);
    } catch (error) {
      if (error?.name === "AbortError") return;
      consoleMessage("RefugeHeader: predictive search failed", "error", error);
    }
  }
  wirePreview() {
    const _preview = this.elements.preview;
    if (!(_preview instanceof HTMLImageElement)) return;
    this.defaultPreview = _preview.src;
    this.defaultCaption = this.elements.caption?.textContent ?? "";
    this.container.querySelectorAll("[data-shop-link]").forEach((link) => {
      this.addListener(link, "pointerenter", () => this.showPreview(link));
      this.addListener(link, "focus", () => this.showPreview(link));
      this.addListener(link, "pointerleave", () => this.resetPreview());
      this.addListener(link, "blur", () => this.resetPreview());
    });
  }
  // The swap is a src change, so a photograph not yet fetched leaves the last
  // one standing until it arrives — on a first hover that reads as the menu not
  // changing at all. Opening the menu fetches every link's photograph once.
  warmPreviews() {
    if (this.previewsWarmed) return;
    this.previewsWarmed = true;
    this.container.querySelectorAll("[data-preview-src]").forEach((link) => {
      const _image = document.createElement("img");
      _image.src = link.dataset.previewSrc ?? "";
    });
  }
  showPreview(link) {
    const _preview = this.elements.preview;
    if (!_preview) return;
    const _src = link.dataset.previewSrc;
    swapPhoto({
      image: _preview,
      swap: () => {
        if (_src) _preview.src = _src;
        _preview.parentElement?.classList.toggle(PRODUCT_PREVIEW, link.dataset.previewFit === "product");
      }
    });
    if (this.elements.caption) {
      this.elements.caption.textContent = link.dataset.previewCaption || link.textContent || "";
    }
  }
  resetPreview() {
    const _preview = this.elements.preview;
    if (!_preview || !this.defaultPreview) return;
    swapPhoto({
      image: _preview,
      swap: () => {
        _preview.src = this.defaultPreview;
        _preview.parentElement?.classList.remove(PRODUCT_PREVIEW);
      }
    });
    if (this.elements.caption) this.elements.caption.textContent = this.defaultCaption;
  }
  destroy() {
    if (this.isDestroyed()) return;
    this.observer?.disconnect();
    this.observer = null;
    this.groupResize?.disconnect();
    this.groupResize = null;
    document.documentElement.style.removeProperty("--refuge-header-height");
    this.cancelHoverClose();
    this.search.cancel();
    this.searchRequest?.abort();
    this.searchRequest = null;
    super.destroy();
  }
}
useSectionLifecycle("refuge-header", {
  onLoad: mountComponent(RefugeHeader),
  onUnload: (_root, instance) => instance?.destroy()
});
const SETTLED_KEY = "refuge:subscribe-modal";
const PAGES_KEY = "refuge:session-pages";
const PENDING_KEY = "refuge:subscribe-modal:pending";
class SubscribeModal extends BaseComponent {
  constructor() {
    super(...arguments);
    this.componentName = "SubscribeModal";
    this.timer = null;
    this.pressedBackdrop = false;
  }
  getDefaultConfig() {
    return { debug: false, delaySeconds: 30, pageThreshold: 2, rememberDays: 365 };
  }
  init() {
    this.elements = cacheElements(this.container, {
      dialog: "[data-subscribe-dialog]",
      close: "[data-subscribe-close]",
      form: "form",
      outcome: "[data-subscribe-outcome]"
    });
    if (!guardElement(this.elements.dialog, "SubscribeModal: dialog not found")) return;
    if (this.elements.close) {
      this.addListener(this.elements.close, "click", () => this.settle("closed"));
    }
    if (this.elements.form) {
      this.addListener(this.elements.form, "submit", () => {
        this.markPending();
        this.settle("subscribed", false);
      });
    }
    this.addListener(this.elements.dialog, "close", () => this.settle("closed", false));
    this.addListener(this.elements.dialog, "pointerdown", (event) => {
      this.pressedBackdrop = event.target === this.elements.dialog;
    });
    this.addListener(this.elements.dialog, "click", (event) => {
      if (!this.pressedBackdrop || event.target !== this.elements.dialog) return;
      this.pressedBackdrop = false;
      this.elements.dialog?.close();
    });
    if (window.Shopify?.designMode) {
      this.log("theme editor — opening only while the section is selected");
      return;
    }
    if (this.elements.outcome && this.takePending()) {
      this.open();
      return;
    }
    if (this.landedOnFormSuccess()) {
      this.log("a form just posted — not interrupting the confirmation");
      return;
    }
    if (this.hasSettled()) {
      this.log("already settled — not showing");
      return;
    }
    if (this.pagesThisSession() >= this.config.pageThreshold) {
      this.open();
      return;
    }
    this.timer = window.setTimeout(() => this.open(), this.config.delaySeconds * 1e3);
  }
  /**
   * Whether this page is the confirmation Shopify renders after a form posts.
   *
   * Shopify appends `contact_posted=true` for contact forms and
   * `customer_posted=true` for customer forms, which is the only way to know:
   * Liquid cannot read query parameters, and `form.posted_successfully?` is
   * scoped to the form block that owns it, not to the page.
   */
  landedOnFormSuccess() {
    try {
      const _params = new URLSearchParams(window.location.search);
      for (const [key, value] of _params.entries()) {
        if (key.endsWith("_posted") && value === "true") return true;
      }
    } catch {
    }
    return false;
  }
  open() {
    const _dialog = this.elements.dialog;
    if (!_dialog?.showModal || _dialog.open) return;
    _dialog.showModal();
    this.log("opened");
  }
  /** The theme editor's preview of this section, opened and shut by selection. */
  preview(open) {
    if (open) {
      this.open();
      return;
    }
    this.elements.dialog?.close();
  }
  settle(reason, close = true) {
    if (window.Shopify?.designMode) {
      if (close) this.elements.dialog?.close();
      return;
    }
    try {
      const _record = { reason, at: Date.now() };
      window.localStorage.setItem(SETTLED_KEY, JSON.stringify(_record));
    } catch {
    }
    if (close) this.elements.dialog?.close();
    this.log(`settled: ${reason}`);
  }
  markPending() {
    try {
      window.sessionStorage.setItem(PENDING_KEY, "1");
    } catch {
    }
  }
  takePending() {
    try {
      if (window.sessionStorage.getItem(PENDING_KEY) === null) return false;
      window.sessionStorage.removeItem(PENDING_KEY);
      return true;
    } catch {
      return false;
    }
  }
  hasSettled() {
    try {
      const _raw = window.localStorage.getItem(SETTLED_KEY);
      if (_raw === null) return false;
      let _at = 0;
      try {
        _at = JSON.parse(_raw)?.at ?? 0;
      } catch {
        return true;
      }
      if (!_at) return true;
      const _days = Number(this.config.rememberDays);
      if (!Number.isFinite(_days) || _days <= 0) return true;
      return Date.now() - _at < _days * 24 * 60 * 60 * 1e3;
    } catch {
      return false;
    }
  }
  pagesThisSession() {
    try {
      const _next = Number(window.sessionStorage.getItem(PAGES_KEY) ?? "0") + 1;
      window.sessionStorage.setItem(PAGES_KEY, String(_next));
      return _next;
    } catch {
      return 1;
    }
  }
  destroy() {
    if (this.isDestroyed()) return;
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
    super.destroy();
  }
}
useSectionLifecycle("subscribe-modal", {
  mount: "idle",
  onLoad: mountComponent(SubscribeModal),
  onUnload: (_root, instance) => instance?.destroy(),
  onSelect: (_root, instance) => instance?.preview(true),
  onDeselect: (_root, instance) => instance?.preview(false)
});
const OPEN_KEYS = ["Enter", " ", "ArrowDown", "ArrowUp"];
class CollectionFilters extends BaseComponent {
  constructor() {
    super(...arguments);
    this.componentName = "CollectionFilters";
    this.form = null;
    this.listboxes = [];
    this.open = null;
  }
  getDefaultConfig() {
    return { debug: false };
  }
  init() {
    this.form = this.container.querySelector("[data-filter-form]");
    if (!guardElement(this.form, "CollectionFilters: filter form not found")) return;
    const _submit = this.form.querySelector('[data-action="filter-submit"]');
    if (_submit) _submit.hidden = true;
    this.addListener(this.form, "change", (event) => {
      const _target = event.target;
      if (!_target?.matches("[data-filter-select]")) return;
      this.apply();
    });
    this.form.querySelectorAll("[data-filter-select]").forEach((select) => {
      this.enhance(select);
    });
    this.addListener(document, "click", (event) => {
      const _target = event.target;
      if (!this.open || !_target) return;
      if (this.open.button.contains(_target) || this.open.panel.contains(_target)) return;
      this.close();
    });
    this.log(`filter bar applying on change, ${this.listboxes.length} listbox(es)`);
  }
  enhance(select) {
    const _control = select.parentElement;
    if (!_control) return;
    const _labelId = `${select.id}-label`;
    const _label = _control.parentElement?.querySelector("label");
    if (_label && !_label.id) _label.id = _labelId;
    const _button = document.createElement("button");
    _button.type = "button";
    _button.className = "refuge-collection-products__filter-button";
    _button.id = `${select.id}-button`;
    _button.setAttribute("aria-haspopup", "listbox");
    _button.setAttribute("aria-expanded", "false");
    if (_label?.id) _button.setAttribute("aria-labelledby", `${_label.id} ${_button.id}`);
    const _panel = document.createElement("div");
    _panel.className = "refuge-collection-products__filter-panel";
    _panel.id = `${select.id}-panel`;
    _panel.setAttribute("role", "listbox");
    _panel.tabIndex = -1;
    _panel.hidden = true;
    if (_label?.id) _panel.setAttribute("aria-labelledby", _label.id);
    _button.setAttribute("aria-controls", _panel.id);
    const _options = Array.from(select.options).map((option, index) => {
      const _item = document.createElement("div");
      _item.className = "refuge-collection-products__filter-option";
      _item.id = `${select.id}-option-${index}`;
      _item.setAttribute("role", "option");
      _item.dataset.value = option.value;
      _item.textContent = option.textContent?.trim() ?? "";
      if (option.disabled) _item.setAttribute("aria-disabled", "true");
      _item.setAttribute("aria-selected", String(option.selected));
      _panel.append(_item);
      return _item;
    });
    const _listbox = {
      select,
      button: _button,
      panel: _panel,
      options: _options,
      active: Math.max(select.selectedIndex, 0)
    };
    _button.textContent = select.options[select.selectedIndex]?.textContent?.trim() ?? "";
    select.hidden = true;
    select.tabIndex = -1;
    _control.append(_button, _panel);
    this.addListener(_button, "click", () => this.toggle(_listbox));
    this.addListener(_button, "keydown", (event) => this.handleButtonKey(event, _listbox));
    this.addListener(_panel, "keydown", (event) => this.handlePanelKey(event, _listbox));
    this.addListener(_panel, "click", (event) => {
      const _item = event.target?.closest('[role="option"]');
      if (!_item) return;
      this.choose(_listbox, _listbox.options.indexOf(_item));
    });
    this.listboxes.push(_listbox);
  }
  handleButtonKey(event, listbox) {
    if (!OPEN_KEYS.includes(event.key)) return;
    event.preventDefault();
    this.show(listbox);
  }
  handlePanelKey(event, listbox) {
    switch (event.key) {
      case "Escape":
        event.preventDefault();
        this.close();
        listbox.button.focus();
        break;
      case "ArrowDown":
        event.preventDefault();
        this.moveActive(listbox, 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.moveActive(listbox, -1);
        break;
      case "Home":
        event.preventDefault();
        this.setActive(listbox, this.nextEnabled(listbox, -1, 1));
        break;
      case "End":
        event.preventDefault();
        this.setActive(listbox, this.nextEnabled(listbox, listbox.options.length, -1));
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        this.choose(listbox, listbox.active);
        break;
      case "Tab":
        this.close();
        break;
    }
  }
  /** Steps past any option the collection has nothing left to show for. */
  nextEnabled(listbox, from, step) {
    for (let _i = from + step; _i >= 0 && _i < listbox.options.length; _i += step) {
      if (!listbox.options[_i]?.hasAttribute("aria-disabled")) return _i;
    }
    return from >= 0 && from < listbox.options.length ? from : listbox.active;
  }
  moveActive(listbox, step) {
    this.setActive(listbox, this.nextEnabled(listbox, listbox.active, step));
  }
  setActive(listbox, index) {
    listbox.active = index;
    listbox.options.forEach((option, i) => option.classList.toggle("is-active", i === index));
    listbox.panel.setAttribute("aria-activedescendant", listbox.options[index]?.id ?? "");
    listbox.options[index]?.scrollIntoView({ block: "nearest" });
  }
  choose(listbox, index) {
    const _option = listbox.options[index];
    if (!_option || _option.hasAttribute("aria-disabled")) return;
    listbox.select.value = _option.dataset.value ?? "";
    listbox.button.textContent = _option.textContent;
    listbox.options.forEach((option, i) => option.setAttribute("aria-selected", String(i === index)));
    this.close();
    listbox.button.focus();
    listbox.select.dispatchEvent(new Event("change", { bubbles: true }));
  }
  toggle(listbox) {
    if (this.open === listbox) {
      this.close();
      return;
    }
    this.show(listbox);
  }
  show(listbox) {
    if (this.open && this.open !== listbox) this.close();
    listbox.panel.hidden = false;
    listbox.button.setAttribute("aria-expanded", "true");
    this.open = listbox;
    this.setActive(listbox, Math.max(listbox.select.selectedIndex, 0));
    listbox.panel.focus();
  }
  close() {
    if (!this.open) return;
    this.open.panel.hidden = true;
    this.open.button.setAttribute("aria-expanded", "false");
    this.open = null;
  }
  apply() {
    if (!this.form) return;
    const _url = new URL(this.form.action, window.location.href);
    for (const [key, value] of new FormData(this.form).entries()) {
      if (typeof value === "string" && value !== "") _url.searchParams.append(key, value);
    }
    window.location.assign(_url.toString());
  }
  destroy() {
    if (this.isDestroyed()) return;
    this.listboxes.forEach(({ select, button, panel }) => {
      button.remove();
      panel.remove();
      select.hidden = false;
      select.removeAttribute("tabindex");
    });
    this.listboxes = [];
    this.open = null;
    super.destroy();
  }
}
useSectionLifecycle("collection-filters", {
  onLoad: mountComponent(CollectionFilters),
  onUnload: (_root, instance) => instance?.destroy()
});
class ProductGallery extends BaseComponent {
  constructor() {
    super(...arguments);
    this.componentName = "ProductGallery";
    this.dots = [];
    this.frames = [];
    this.onScroll = Object.assign(() => {
    }, {
      cancel: () => {
      }
    });
    this.current = 0;
    this.syncing = false;
  }
  getDefaultConfig() {
    return { debug: false };
  }
  init() {
    this.elements = cacheElements(this.container, {
      track: "[data-gallery-track]",
      dots: "[data-gallery-dots]",
      prev: "[data-gallery-prev]",
      next: "[data-gallery-next]"
    });
    if (!guardElement(this.elements.track, "ProductGallery: track not found")) return;
    if (!this.elements.dots) return;
    this.dots = Array.from(this.elements.dots.querySelectorAll("[data-gallery-dot]"));
    if (this.dots.length < 2) return;
    this.frames = Array.from(this.elements.track.querySelectorAll("[data-gallery-frame]"));
    const _sectionId = this.container.closest("[data-section-id]")?.dataset.sectionId;
    this.addListener(document, "change", (event) => {
      const _input = event.target?.closest(
        '[data-colors] input[type="radio"]'
      );
      if (!_input?.checked) return;
      if (this.syncing) return;
      const _origin = _input.closest("[data-section-id]")?.dataset.sectionId;
      if (_sectionId && _origin && _origin !== _sectionId) return;
      this.showColor(_input.value);
    });
    this.addListener(this.elements.dots, "click", (event) => {
      const _dot = event.target.closest("[data-gallery-dot]");
      if (!_dot) return;
      this.scrollTo(Number(_dot.dataset.index ?? "0"));
    });
    if (this.elements.prev) {
      this.addListener(this.elements.prev, "click", () => this.step(-1));
    }
    if (this.elements.next) {
      this.addListener(this.elements.next, "click", () => this.step(1));
    }
    this.onScroll = useDebounce(() => this.mark(this.nearestFrame()), 80);
    this.addListener(this.elements.track, "scroll", this.onScroll, { passive: true });
    this.markEnds();
  }
  /**
   * Brings the first frame of a colourway into view.
   *
   * The gallery is not filtered — every photograph stays reachable — so this
   * only moves to the one that matches. A colourway with no frame of its own
   * leaves the gallery where it is, which is why an unmatched value is a quiet
   * return rather than a jump to the first frame.
   *
   * A swatch is a different question from an arrow. The arrows, markers and a
   * swipe travel through the photographs; a swatch asks for one colour, so the
   * gallery cuts straight to it under a crossfade rather than sliding past
   * every frame in between.
   */
  showColor(value) {
    const _wanted = value.trim().toLowerCase();
    if (!_wanted) return;
    const _index = this.frames.findIndex((frame) => frame.dataset.galleryColor === _wanted);
    if (_index < 0) {
      this.log(`no frame for ${_wanted}`);
      return;
    }
    if (_index === this.current) return;
    const _from = this.frames[this.current]?.querySelector("img");
    const _to = this.frames[_index]?.querySelector("img") ?? null;
    if (!_from || !this.elements.track) {
      this.scrollTo(_index);
      return;
    }
    swapPhoto({
      image: _from,
      after: this.elements.track,
      incoming: () => _to,
      swap: () => this.scrollTo(_index, true)
    });
  }
  /**
   * Points the swatches at whichever colourway has been scrolled to.
   *
   * The other half of showColor(), so the two stay in step however the gallery
   * was moved — an arrow, a marker, a swipe. `syncing` is what stops the echo:
   * checking the radio fires a change event that would scroll the gallery back
   * to the first frame of that colour, undoing a swipe mid-gesture.
   *
   * A frame with no colourway — the fabric close-ups — leaves the swatches
   * alone rather than clearing them, since it belongs to no one colour.
   */
  selectColor(color) {
    if (!color) return;
    const _section = this.container.closest("[data-section-id]")?.parentElement;
    const _group = _section?.querySelector("[data-colors]");
    if (!_group) return;
    const _input = Array.from(_group.querySelectorAll('input[type="radio"]')).find(
      (radio) => radio.value.trim().toLowerCase() === color
    );
    if (!_input || _input.checked) return;
    this.syncing = true;
    _input.checked = true;
    _input.dispatchEvent(new Event("change", { bubbles: true }));
    this.syncing = false;
  }
  /** One frame either way, clamped — the arrows do not wrap. */
  step(delta) {
    const _next = Math.min(Math.max(this.current + delta, 0), this.dots.length - 1);
    if (_next === this.current) return;
    this.scrollTo(_next);
  }
  /** Disables whichever arrow has nowhere left to go. */
  markEnds() {
    const _atStart = this.current <= 0;
    const _atEnd = this.current >= this.dots.length - 1;
    if (this.elements.prev instanceof HTMLButtonElement) this.elements.prev.disabled = _atStart;
    if (this.elements.next instanceof HTMLButtonElement) this.elements.next.disabled = _atEnd;
  }
  scrollTo(index, cut = false) {
    const _track = this.elements.track;
    if (!_track) return;
    const _reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const _behavior = cut ? "instant" : _reduced ? "auto" : "smooth";
    _track.scrollTo({ left: index * _track.clientWidth, behavior: _behavior });
    this.mark(index);
  }
  nearestFrame() {
    const _track = this.elements.track;
    if (!_track || _track.clientWidth === 0) return this.current;
    const _index = Math.round(_track.scrollLeft / _track.clientWidth);
    return Math.min(Math.max(_index, 0), this.dots.length - 1);
  }
  mark(index) {
    if (index === this.current) return;
    this.current = index;
    this.dots.forEach((dot, position) => {
      if (position === index) {
        dot.setAttribute("aria-current", "true");
      } else {
        dot.removeAttribute("aria-current");
      }
    });
    this.markEnds();
    this.selectColor(this.frames[index]?.dataset.galleryColor);
    this.log(`frame ${index + 1} of ${this.dots.length}`);
  }
  destroy() {
    if (this.isDestroyed()) return;
    this.onScroll.cancel();
    super.destroy();
  }
}
useSectionLifecycle("product-gallery", {
  mount: "visible",
  onLoad: mountComponent(ProductGallery),
  onUnload: (_root, instance) => instance?.destroy()
});
const EMAIL_FIELD = "contact[email]";
class ContactForm extends BaseComponent {
  constructor() {
    super(...arguments);
    this.componentName = "ContactForm";
    this.submitting = false;
  }
  getDefaultConfig() {
    return { debug: false, newsletterTag: "newsletter" };
  }
  init() {
    this.elements = cacheElements(this.container, {
      form: "[data-contact-form]",
      consent: "[data-contact-consent]"
    });
    if (!guardElement(this.elements.form, "ContactForm: form not found")) return;
    if (!this.elements.consent) return;
    this.addListener(this.elements.form, "submit", (event) => this.onSubmit(event));
  }
  onSubmit(event) {
    const _form = this.elements.form;
    if (this.submitting) return;
    const _consent = this.elements.consent;
    const _email = _form.elements.namedItem(EMAIL_FIELD);
    const _address = _email?.value.trim();
    if (!_consent?.checked || !_address) return;
    event.preventDefault();
    this.submitting = true;
    void this.subscribe(_form.action, _address).finally(() => _form.requestSubmit());
  }
  // The endpoint is the contact form's own action, so nothing here has to know
  // the shop's locale prefix — Liquid already wrote it into the markup.
  async subscribe(action, email) {
    const _body = new URLSearchParams({
      form_type: "customer",
      utf8: "✓",
      "contact[email]": email,
      "contact[tags]": this.config.newsletterTag
    });
    try {
      await fetch(action, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: _body
      });
      this.log(`subscribed ${email}`);
    } catch (error) {
      consoleMessage("ContactForm: newsletter signup failed", "error", error);
    }
  }
}
useSectionLifecycle("contact-form", {
  mount: "visible",
  onLoad: mountComponent(ContactForm),
  onUnload: (_root, instance) => instance?.destroy()
});
const ACTIVE = "refuge-button--solid";
const IDLE = "refuge-button--outline";
class SizeGuide extends BaseComponent {
  constructor() {
    super(...arguments);
    this.componentName = "SizeGuide";
    this.tabs = [];
    this.panels = [];
  }
  getDefaultConfig() {
    return { debug: false };
  }
  init() {
    this.elements = cacheElements(this.container, {
      tablist: "[data-tablist]",
      units: "[data-units]"
    });
    if (!guardElement(this.elements.tablist, "SizeGuide: tablist not found")) return;
    this.tabs = Array.from(this.container.querySelectorAll("[data-tab]"));
    this.panels = Array.from(this.container.querySelectorAll("[data-panel]"));
    if (this.tabs.length === 0 || this.tabs.length !== this.panels.length) {
      this.log(`not enhancing: ${this.tabs.length} tabs against ${this.panels.length} panels`);
      return;
    }
    this.container.classList.add("is-enhanced");
    this.elements.tablist.hidden = false;
    if (this.elements.units) this.elements.units.hidden = false;
    this.tabs.forEach((tab, index) => {
      this.addListener(tab, "click", () => this.select(index));
      this.addListener(tab, "keydown", (event) => this.onKeydown(event, index));
    });
    this.container.querySelectorAll("[data-unit-option]").forEach((button) => {
      this.addListener(button, "click", () => this.setUnit(button.dataset.unitOption ?? "in"));
    });
    this.select(0);
  }
  select(index) {
    this.tabs.forEach((tab, i) => {
      const _isActive = i === index;
      tab.setAttribute("aria-selected", String(_isActive));
      tab.tabIndex = _isActive ? 0 : -1;
      tab.classList.toggle(ACTIVE, _isActive);
      tab.classList.toggle(IDLE, !_isActive);
      this.panels[i].hidden = !_isActive;
    });
  }
  /**
   * The tab pattern's own keys. Left and right wrap, Home and End jump, and
   * focus moves with the selection because only one panel is ever shown.
   */
  onKeydown(event, index) {
    const _last = this.tabs.length - 1;
    let _next = null;
    if (event.key === "ArrowRight") _next = index === _last ? 0 : index + 1;
    if (event.key === "ArrowLeft") _next = index === 0 ? _last : index - 1;
    if (event.key === "Home") _next = 0;
    if (event.key === "End") _next = _last;
    if (_next === null) return;
    event.preventDefault();
    this.select(_next);
    this.tabs[_next].focus();
  }
  setUnit(unit) {
    this.container.dataset.unit = unit;
    this.container.querySelectorAll("[data-unit-option]").forEach((button) => {
      const _isActive = button.dataset.unitOption === unit;
      button.setAttribute("aria-pressed", String(_isActive));
      button.classList.toggle(ACTIVE, _isActive);
      button.classList.toggle(IDLE, !_isActive);
    });
    this.log(`unit ${unit}`);
  }
}
useSectionLifecycle("size-guide", {
  mount: "visible",
  onLoad: mountComponent(SizeGuide),
  onUnload: (_root, instance) => instance?.destroy()
});
const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
class RefugeHeroVideo extends BaseComponent {
  constructor() {
    super(...arguments);
    this.componentName = "RefugeHeroVideo";
    this.video = null;
    this.cancelVisible = null;
    this.trimEnd = 0;
  }
  getDefaultConfig() {
    return { debug: false };
  }
  init() {
    this.elements = cacheElements(this.container, { video: "[data-hero-video]" });
    if (!guardElement(this.elements.video, "RefugeHeroVideo: video not found")) return;
    this.video = this.elements.video;
    if (window.matchMedia(REDUCED_MOTION).matches) {
      this.log("reduced motion — the photograph stands, and nothing is fetched");
      return;
    }
    if (this.metered()) {
      this.log("data saver or a slow connection — the photograph stands");
      return;
    }
    this.video.muted = true;
    this.addListener(this.video, "playing", () => this.container.classList.add("is-video-playing"));
    this.trimEnd = Number(this.video.dataset.heroVideoTrim) || 0;
    if (this.trimEnd > 0) {
      this.addListener(this.video, "timeupdate", () => this.restartBeforeTail());
    }
    this.load();
  }
  /**
   * Shopify transcodes to several mp4 renditions but the storefront `video`
   * object exposes only one of them — the 1080p — where the Admin API lists
   * all three. So this usually has a single candidate, and picks it.
   *
   * It stays a choice rather than a hard-coded read for two reasons: a browser
   * takes the first `<source>` it can play rather than the one that fits, so
   * the decision has to live somewhere; and if the platform ever hands back
   * more, the narrowest rendition covering the frame is already what wins.
   */
  pick() {
    const _sources = [...this.container.querySelectorAll("[data-hero-video-source]")].map((node) => ({
      url: node.dataset.heroVideoSource ?? "",
      width: Number(node.dataset.width) || 0
    })).filter((source) => source.url).sort((a, b) => a.width - b.width);
    if (_sources.length === 0) return null;
    const _needed = this.container.clientWidth * Math.min(window.devicePixelRatio || 1, 2);
    return (_sources.find((source) => source.width >= _needed) ?? _sources[_sources.length - 1]).url;
  }
  restartBeforeTail() {
    const _video = this.video;
    if (!_video) return;
    const _duration = _video.duration;
    if (!Number.isFinite(_duration) || _duration <= this.trimEnd) return;
    if (_video.currentTime >= _duration - this.trimEnd) {
      _video.currentTime = 0;
      void this.play();
    }
  }
  metered() {
    const _connection = window.navigator.connection;
    if (!_connection) return false;
    return _connection.saveData === true || /(^|-)2g$/.test(_connection.effectiveType ?? "");
  }
  load() {
    const _video = this.video;
    if (!_video) return;
    const _src = this.pick();
    if (!_src) {
      consoleMessage("RefugeHeroVideo: no sources to choose from", "error");
      return;
    }
    _video.src = _src;
    this.log(`source ${_src.split("/").pop()}`);
    _video.load();
    void this.play();
    this.watch();
  }
  watch() {
    if (!this.video) return;
    this.cancelVisible?.();
    this.cancelVisible = whenVisible(
      this.container,
      () => {
        void this.play();
        this.watch();
      },
      "0px"
    );
  }
  async play() {
    if (!this.video) return;
    try {
      await this.video.play();
    } catch (error) {
      consoleMessage("RefugeHeroVideo: autoplay refused", "error", error);
    }
  }
  destroy() {
    if (this.isDestroyed()) return;
    this.cancelVisible?.();
    this.cancelVisible = null;
    this.video?.pause();
    super.destroy();
  }
}
useSectionLifecycle("hero-video", {
  onLoad: mountComponent(RefugeHeroVideo),
  onUnload: (_root, instance) => instance?.destroy()
});
const ADD_FAILED = "Those pieces could not be added to your bag.";
class RefugePiecesAdd extends BaseComponent {
  constructor() {
    super(...arguments);
    this.componentName = "RefugePiecesAdd";
  }
  getDefaultConfig() {
    return { debug: false };
  }
  init() {
    this.elements = cacheElements(this.container, {
      form: "[data-pieces-add]",
      error: "[data-pieces-error]"
    });
    if (!guardElement(this.elements.form, "RefugePiecesAdd: form not found")) return;
    this.addListener(this.elements.form, "submit", (event) => {
      event.preventDefault();
      void this.submit(this.elements.form);
    });
  }
  items(form) {
    return [...form.querySelectorAll('input[name$="[id]"]')].map((input) => Number(input.value)).filter((id) => Number.isFinite(id) && id > 0).map((id) => ({ id, quantity: 1 }));
  }
  async submit(form) {
    const _items = this.items(form);
    if (_items.length === 0) return;
    const _label = form.dataset.setLabel ?? "";
    let _resolve = () => {
    };
    const _promise = new Promise((resolve) => {
      _resolve = resolve;
    });
    form.dispatchEvent(new CartLinesUpdateSignal(_promise));
    const _fail = (message) => {
      form.dispatchEvent(new CartErrorSignal(message));
      _resolve({ cart: null, detail: { didError: true, source: "refuge-product-pieces" } });
      this.showError(message);
    };
    try {
      const _response = await fetch(`${form.action}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          items: _items.map((item) => ({
            ...item,
            properties: { _set: _label, _set_label: _label }
          })),
          sections: CART_DRAWER_SECTION,
          sections_url: window.location.pathname
        })
      });
      const _payload = await _response.json();
      if (!_response.ok) {
        _fail(_payload.description || _payload.message || ADD_FAILED);
        return;
      }
      this.hideError();
      _resolve({ cart: null, detail: { sections: _payload.sections, source: "refuge-product-pieces" } });
      this.log(`added ${_items.length} pieces`);
    } catch (error) {
      consoleMessage("RefugePiecesAdd: add failed", "error", error);
      _fail(ADD_FAILED);
    }
  }
  showError(message) {
    const _error = this.elements.error;
    if (!_error) return;
    _error.textContent = message;
    _error.hidden = false;
  }
  hideError() {
    if (this.elements.error) this.elements.error.hidden = true;
  }
}
useSectionLifecycle("pieces-add", {
  mount: "visible",
  onLoad: mountComponent(RefugePiecesAdd),
  onUnload: (_root, instance) => instance?.destroy()
});
const ITEM_SELECTOR = ".instafeed-new-layout-item";
const CAPTION_CLASS = "refuge-instagram-strip__caption";
const DEFAULT_LABEL = "View on Instagram";
class RefugeInstagramStrip extends BaseComponent {
  constructor() {
    super(...arguments);
    this.componentName = "RefugeInstagramStrip";
    this.observer = null;
  }
  getDefaultConfig() {
    return { debug: false };
  }
  init() {
    this.elements = cacheElements(this.container, {
      slot: "[data-instagram-profile]"
    });
    const _slot = this.elements.slot;
    if (!guardElement(_slot, "RefugeInstagramStrip: no app slot, so the theme tiles are rendering")) {
      return;
    }
    const _href = _slot.dataset.instagramProfile;
    if (!_href) {
      this.log("no profile link set, so the caption would go nowhere");
      return;
    }
    this.decorate(_slot, _href);
    this.observer = new MutationObserver(() => this.decorate(_slot, _href));
    this.observer.observe(_slot, { childList: true, subtree: true });
  }
  decorate(slot, href) {
    const _label = slot.dataset.instagramHandle || DEFAULT_LABEL;
    const _hover = slot.dataset.instagramHover || DEFAULT_LABEL;
    slot.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
      if (item.querySelector(`.${CAPTION_CLASS}`)) return;
      const _caption = document.createElement("span");
      _caption.className = CAPTION_CLASS;
      const _handle = document.createElement("span");
      _handle.className = `${CAPTION_CLASS}-handle`;
      _handle.textContent = _label;
      const _hoverLabel = document.createElement("span");
      _hoverLabel.className = `${CAPTION_CLASS}-hover`;
      _hoverLabel.setAttribute("aria-hidden", "true");
      _hoverLabel.textContent = _hover;
      const _link = document.createElement("a");
      _link.className = `${CAPTION_CLASS}-link`;
      _link.href = href;
      _link.target = "_blank";
      _link.rel = "noopener noreferrer";
      _link.setAttribute("aria-label", `${_hover} — ${_label}`);
      _caption.append(_handle, _hoverLabel, _link);
      item.append(_caption);
      item.classList.add("is-linked");
    });
  }
  destroy() {
    if (this.isDestroyed()) return;
    this.observer?.disconnect();
    this.observer = null;
    super.destroy();
  }
}
useSectionLifecycle("instagram-strip", {
  mount: "visible",
  onLoad: mountComponent(RefugeInstagramStrip),
  onUnload: (_root, instance) => instance?.destroy()
});
const DEV_MODE_KEY = "refuge:devMode";
function readRenderedCartCount() {
  const _shown = document.querySelector("[data-cart-count]")?.textContent?.trim();
  const _count = Number(_shown);
  return Number.isFinite(_count) ? _count : 0;
}
const IS_LOCAL_BUILD = false;
const THEME_SETTINGS = readThemeSettings();
function resolveDevMode() {
  try {
    const _stored = window.localStorage.getItem(DEV_MODE_KEY);
    if (_stored !== null) return _stored === "true";
  } catch {
  }
  return window.Shopify?.designMode === true || THEME_SETTINGS.debugLogging;
}
window.STOREFRONT = {
  version: "1.0.0",
  settings: {
    devMode: resolveDevMode(),
    theme: THEME_SETTINGS
  },
  theme: {
    shopName: window.Shopify?.shop ?? "refuge",
    currency: window.Shopify?.currency?.active ?? "USD",
    moneyFormat: window.theme?.moneyFormat ?? "${{amount}}"
  },
  cart: {
    // Read from the bag itself, which Liquid rendered with `cart.item_count`.
    // `window.Shopify.cart` is not a global Shopify sets — it was always
    // undefined, so this started at 0 on every page and the optimistic count
    // in cartDrawer.ts added to nothing. A cart with three things in it read
    // "(1)" after the next add, every time.
    count: readRenderedCartCount(),
    total: window.Shopify?.cart?.total_price ?? 0
  },
  events: new EventTarget(),
  debug: {
    info: () => ({
      ...window.STOREFRONT,
      source: "compiled theme asset"
    }),
    sections: () => listSections(),
    vitals: () => getWebVitals(),
    privacy: () => readPrivacyState(),
    devMode: (enabled) => {
      if (enabled !== void 0) {
        window.STOREFRONT.settings.devMode = enabled;
        try {
          window.localStorage.setItem(DEV_MODE_KEY, String(enabled));
        } catch {
        }
      }
      return window.STOREFRONT.settings.devMode;
    }
  }
};
function revealFailsafe() {
  window.setTimeout(() => {
    const _stranded = document.querySelectorAll(
      "[data-reveal]:not(.is-revealed):not([data-reveal-bound])"
    );
    if (_stranded.length === 0) return;
    _stranded.forEach((element) => element.classList.add("is-revealed"));
    consoleMessage(
      `Revealed ${_stranded.length} element(s) that were never reached`,
      "error",
      Array.from(
        _stranded,
        (element) => element.closest("[data-section-type]")?.dataset.sectionType
      )
    );
  }, 5e3);
}
function initializeApp() {
  revealFailsafe();
  try {
    initGlobalEvents();
    initPrivacyChoices();
    initCardColors();
    initKlaviyoSubscribe({
      companyId: THEME_SETTINGS.klaviyoCompanyId,
      listId: THEME_SETTINGS.klaviyoListId
    });
    initQuickShopTouch();
    initAddToBagFeedback();
    reportWebVitals();
    registerSectionLifecycles();
    consoleMessage("Storefront ready", "success", {
      version: window.STOREFRONT.version,
      source: IS_LOCAL_BUILD ? "local vite dev server" : "compiled theme asset",
      sections: listSections()
    });
  } catch (error) {
    consoleMessage("Initialisation failed", "error", error);
  }
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeApp, { once: true });
} else {
  initializeApp();
}
